/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TRecordingsUpdateLastViewedPositionParams } from "@tivocorp/data-domain-lib";
import type { LastViewedPosition } from "@tivocorp/data-domain-lib/client-code/watch-history";

import {
  MediaPaneType,
  MetadataState,
  MetadataType,
  PlaybackContentType,
  PlaybackRestrictionType,
  PlayerErrorSeverity,
  PlayerState,
  PlayerViewMode,
  SessionState,
  VideoProviderState
} from "./enums";
import {
  PlaybackError,
  PlaybackErrorMapper,
  PlaybackSetupErrorCode,
  isBlockingMetadataError,
  playerErrorCodes
} from "./error";
import { DrmManager } from "./impls/drm/DrmManager";
import { ShakaVideoPlayerImpl } from "./impls";
import type {
  IMetadata,
  IPlayable,
  IPlaybackError,
  IPlaybackIntervalInfo,
  ISessionCreateModel
} from "./interfaces";
import type { ISubscription } from "./ISubscription";
import { SubscriptionModel } from "./SubscriptionModel";
import {
  MetadataManager,
  PlaybackURLManager,
  RestrictionManager,
  SessionManager,
  VideoManager
} from "./managers";
import { TrickPlayManager } from "./trickplay";
import { Direction, SpeedLevel, TrickPlayMode } from "./trickplay";
import type { ITrickPlayConfig, ITrickPlayEligibility } from "./trickplay";
import {
  DEFAULT_TRICKPLAY_CONFIG,
  DISABLED_ELIGIBILITY,
  MIN_TIMESHIFT_WINDOW_SECONDS,
  deriveEligibility,
  parseTrickModeRestrictions
} from "./trickplay";
import { TivoPlusUrlResolver } from "./tivoplus";
import { AiringPreviewUtils } from "./utils/AiringPreviewUtils";
import type {
  TActiveDrmInfo,
  TAudioTrack,
  TPigConfig,
  TPlayerError,
  TPlayerStats,
  TSeekRange,
  TStandalonePlaybackEvent,
  TTextTrack,
  TWhisperEvent
} from "./types";
import { Log } from "../logging/Log";
import { Diagnostics } from "../logging";
import { VideoProviderConfigurationLogger } from "../logging/VideoProviderConfigurationLogger";
import {
  PlaybackSessionCoordinator,
  SessionEndReason
} from "../analytics/playbackSession";
import type { TPlaybackSessionContext } from "../analytics/playbackSession";
import { InHomeLocationModel } from "../device";
import {
  ChannelsInteractor,
  RecordingsServiceInteractor,
  WatchHistoryInteractor
} from "../interactor";
import type { Session } from "../entities";
import { DeviceModel } from "../device/DeviceModel";
import {
  StorageAdapter,
  STORAGE_KEY_USER_PREFERRED_AUDIO,
  STORAGE_KEY_USER_PREFERRED_CC,
  STORAGE_KEY_IS_TEXT_TRACK_ENABLED,
  STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION
} from "../storage";
import { GracePeriodUtils } from "../utility/GracePeriodUtils";
import {
  IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD,
  SESSION_MANAGER_GRACE_PERIOD,
  APP_BACKGROUND_MAX_MS,
  MILLISECONDS_IN_A_SECOND,
  LIVE_WATCH_HISTORY_DELAY_MS,
  LIVE_WATCH_HISTORY_JITTER_MAX_MS
} from "../../config";
import { TLiveWatchHistoryItem } from "../types";
import {
  PlaybackUrlUtils,
  TrackPreferenceUtils,
  TrackUtils,
  VodPlaybackUtils
} from "./utils";
import type { TAudioTrackPreference, TTextTrackPreference } from "./utils";
import { Locale, LocaleKeys } from "../locale";
import { Branding } from "../branding";

const LAST_VIEWED_POSITION_INTERVAL_TIME: number =
  2 * 60 * MILLISECONDS_IN_A_SECOND;
const MAX_DRM_ERROR_RETRY_COUNT: number = 2;
const PROGRAM_BOUNDARY_RETRY_DELAY_MS: number = 1000;
// Backoff for retrying the post-reconnect airing refresh: 1s, 2s, 4s, 8s, 16s.
const RECONNECT_REFRESH_RETRY_BASE_DELAY_MS: number = 1000;
const MAX_RECONNECT_REFRESH_RETRY_COUNT: number = 5;
const CONTENT_ID_PATTERN: RegExp = /^tivo(?::|%3A)ct(\.ts)?\.\d+$/i;
const STORE_SELECTED_AUDIO_TRACK_PREFERENCE_METHOD: string =
  "storeSelectedAudioTrackPreference()";

export type BeforeNavigateHandler = (targetUrl: string | null) => void;

const getActionKernel = (
  action: unknown
): Record<string, unknown> | undefined => {
  if (typeof action !== "object" || action === null || !("kernel" in action)) {
    return undefined;
  }

  const kernel: unknown = action.kernel;
  if (typeof kernel !== "object" || kernel === null) {
    return undefined;
  }

  return kernel as Record<string, unknown>;
};

/**
 * Central orchestrator for the video playback subsystem.
 * Singleton — coordinates VideoManager, DrmManager, SessionManager,
 * MetadataManager, and RestrictionManager.
 *
 * Ported from kaveri-web-app src/core/videoplayer/VideoPlaybackController.ts
 * with adaptations for the Unity IPlayer API.
 */
export class VideoPlaybackController {
  private static readonly TAG: string = "VideoPlaybackController";
  private static readonly LOG_IS_FORCED_PAUSED = ", isForcedPaused=";
  private static readonly LOG_IS_PLAYER_LOADED = ", isPlayerLoaded=";

  private static mInstance: VideoPlaybackController | null = null;

  // ── Subscriptions ────────────────────────────────────────────────────────

  private mReadySubscription: SubscriptionModel<boolean> =
    new SubscriptionModel<boolean>();

  private mMetadataStateSubscription: SubscriptionModel<MetadataState> =
    new SubscriptionModel<MetadataState>();

  private mPlaybackRestrictionTypeSubscription: SubscriptionModel<PlaybackRestrictionType> =
    new SubscriptionModel<PlaybackRestrictionType>();

  private mSessionStateSubscription: SubscriptionModel<SessionState> =
    new SubscriptionModel<SessionState>();

  private mPlaybackErrorSubscription: SubscriptionModel<PlaybackError> =
    new SubscriptionModel<PlaybackError>();

  private mPlayerViewModeSubscription: SubscriptionModel<PlayerViewMode> =
    new SubscriptionModel<PlayerViewMode>();

  private mPlayerStateSubscription: SubscriptionModel<PlayerState> =
    new SubscriptionModel<PlayerState>();

  private mProgramBoundarySubscription: SubscriptionModel<void> =
    new SubscriptionModel<void>();

  // Fired when full-screen playback must leave the player and return to the
  // previous screen: a long-background return (Catchup / nDVR / VOD), or a
  // VOD / nDVR program reaching its end (exitEndedProgram()). The view layer
  // subscribes and performs the actual navigation (navigateBack()).
  private mExitToPreviousScreenSubscription: SubscriptionModel<void> =
    new SubscriptionModel<void>();

  private mPlayableChangeSubscription: SubscriptionModel<IPlayable> =
    new SubscriptionModel<IPlayable>();

  private mWhisperEventSubscription: SubscriptionModel<TWhisperEvent> =
    new SubscriptionModel<TWhisperEvent>();

  /** Standalone media playback (EAS alert tone and similar). */
  private mStandalonePlaybackEventSubscription: SubscriptionModel<TStandalonePlaybackEvent> =
    new SubscriptionModel<TStandalonePlaybackEvent>();

  /** 5-min "watched" threshold. Cancelled by stop / channel change. */
  private mLiveWatchThresholdTimerId: ReturnType<typeof setTimeout> | null =
    null;

  /** Post-threshold jitter timers. Survive stop / screen switch;
   *  each posts a snapshotted item. Cleared only on full teardown.
   */
  private mLiveWatchJitterTimerIds: Set<ReturnType<typeof setTimeout>> =
    new Set();

  // ── Managers ─────────────────────────────────────────────────────────────

  private mDrmManager: DrmManager;

  private mMetadataManager: MetadataManager;

  private mPlaybackURLManager: PlaybackURLManager;

  private mRestrictionManager: RestrictionManager;

  private mSessionManager: SessionManager;

  private mTrackPrefereneceUtils;

  private mVideoManager: VideoManager;

  private mPlaybackSessionCoordinator: PlaybackSessionCoordinator;

  // ── State Fields ─────────────────────────────────────────────────────────
  private mAudioTracksMap: { id: number; label: string }[];

  // Wall-clock timestamp (ms) captured when the app moved to background;
  // null while foregrounded. Used to detect a "long background" on return.
  private mBackgroundedAtMs: number | null = null;

  private mGracePeriodTimerId: ReturnType<typeof setTimeout> | null = null;

  private mIsAntiburnActive: boolean = false;

  private mIsGracePeriodFinished: boolean = false;

  private mIsForcedPaused: boolean = false;

  /**
   * Inhibits the live auto-resume timer while something external owns the
   * pause. Set by the EAS path for the life of an alert — see
   * {@link setLiveAutoResumeSuppressed}.
   */
  private mIsLiveAutoResumeSuppressed: boolean = false;

  /**
   * Minimum delay (ms) for the live auto-resume timer
   */
  private static readonly LIVE_RESUME_MIN_DELAY_MS: number = 500;

  /**
   * One-shot timer scheduled while paused on LIVE content. Fires once the
   * DVR window start is computed to have caught up to the paused position,
   * auto-resuming playback. Mirrors mobi's MediaController auto-resume
   * timer approach.
   */
  private mLiveResumeTimerId: ReturnType<typeof setTimeout> | null = null;

  private mIsMetadataLoaded: boolean = false;

  /**
   * Whether {@link initPlayer} has already bound this controller's listeners.
   *
   * They are bound once for the lifetime of the singleton, never per mount.
   */
  private mArePlayerListenersBound: boolean = false;

  private mIsNetworkConnected: boolean = true;

  private mIsPlayerLoaded: boolean = false;

  private mIsPlaying: boolean = false;

  /**
   * Advanced by every play() and every teardown. Async continuations of a
   * tune capture it and stand down once it has moved on, so a late answer
   * for a channel the user has already left cannot set that channel up again.
   */
  private mTuneGeneration: number = 0;

  /** A tune has started and has neither reached PLAYING nor been torn down. */
  private mIsTuneInProgress: boolean = false;

  /**
   * The tune generation a DRM setup was requested for, until its SUCCESS is
   * consumed. DRM SUCCESS is a broadcast — the start-up token prefetch and
   * superseded setups send it too — so only the first one for the current
   * request is acted on; a second would load the stream and create a session
   * again.
   */
  private mDrmRequestGeneration: number | null = null;

  private mHasPreviousContentPlayed: boolean = false;

  private mIsReady: boolean = false;

  private mLVPIntervalId: ReturnType<typeof setInterval> | null = null;

  private mPlayable: IPlayable | null = null;

  /**
   * The session that answered for the current content, or `null` until it
   * has. Set in onSessionSuccess() and cleared on every new tune, so a
   * reader never sees the previous content's session — SessionManager keeps
   * handing that one out until the next session replaces it.
   */
  private mActiveSession: Session | null = null;

  private mPlayerError: PlaybackError | null = null;

  private mPlayerViewMode: PlayerViewMode = PlayerViewMode.PIP;

  /**
   * Deferred resume intent for PlayerViewMode.Hidden.
   *
   * Set when hidden() pauses an actively-playing stream, and by any resume()
   * attempt that arrives while hidden (app foreground, network reconnect,
   * restriction lifted). Consumed exactly once by restoreFromHidden() when a
   * screen claims a visible view mode. Deliberately NOT reset by cleanPlayer(),
   * so a stream torn down while hidden (e.g. long background) is still restored
   * on return.
   */
  private mWasPlayingBeforeHidden: boolean = false;
  private mIsClosedCaptionHiddenForViewMode: boolean = false;

  private mBeforeNavigateHandler: BeforeNavigateHandler | null = null;

  private mProgramBoundaryTimeoutId: ReturnType<typeof setTimeout> | null =
    null;

  private mRestrictionType: PlaybackRestrictionType =
    PlaybackRestrictionType.NO_RESTRICTION;

  private mSeekRangeStartTime: number = 0;

  /** Whether a dedicated Start Over (SOCU) stream is available. Set from preview data. */
  private mStartOverAvailable: boolean = false;

  /**
   * The session trick-play eligibility was last derived from, so it can be
   * re-derived when the player's seekable window changes without a new
   * session. Null until eligibility is first applied.
   */
  private mTrickPlayEligibilitySession: Session | null = null;

  /** Seekable-window state the current trick-play eligibility was derived with. */
  private mHasSeekableWindow: boolean = false;

  /**
   * Resume position (seconds from the Start Over stream start) to seek to when
   * starting STARTOVER_RESUME playback. Computed from the live playhead and the
   * programme start time at the moment the switch is triggered. `null` for
   * STARTOVER_BEGINNING (start from the beginning).
   */
  private mStartOverResumeSeconds: number | null = null;

  /**
   * SOCU position to return to once connectivity is back, captured when the
   * network drops. Reconnecting re-runs the SOCU metadata → DRM → load chain,
   * which reloads the stream from the start of its window (and, for
   * STARTOVER_BEGINNING, seeks there explicitly at first frame), so without
   * this the viewer is sent back to the beginning. Held against the playable
   * it was captured for: a different playable never inherits it.
   *
   * `applied` marks that the reload's first frame has seeked to it. It is kept
   * until that reload reaches PLAYING rather than dropped at once, because
   * onFirstFrame() runs twice per load (it is reached from both the player
   * state and the first-frame subscriptions) and the second run must not fall
   * through to the Start Over seek back to the window start.
   */
  private mNetworkRestoreSeek: {
    playable: IPlayable;
    offsetSeconds: number;
    applied: boolean;
  } | null = null;

  /**
   * The airing refresh restorePlayback() runs on reconnect, while it may still
   * be retried. The `online` event can fire before the new connection carries
   * traffic (an Ethernet ↔ Wi-Fi switch brings the link up ahead of DHCP/DNS),
   * so that refresh can fail even though connectivity is moments away — and
   * since `navigator.onLine` is already true, no further reconnect follows to
   * try again. Held against the playable it was started for: a different
   * playable (channel change) never inherits it.
   */
  private mReconnectRefresh: {
    playable: IPlayable;
    attempt: number;
  } | null = null;

  private mReconnectRefreshTimerId: ReturnType<typeof setTimeout> | null = null;

  private mSessionManagerGracePeriodTimeoutId: ReturnType<
    typeof setTimeout
  > | null = null;

  private mTextTracks: TTextTrack[] = [];

  private mVideoPlayerCurrentState: PlayerState | null = null;

  /** Non-serializable browser PiP lifecycle state. */
  private mBrowserPiPActive: boolean = false;
  private mPlayingOnPiPEntry: boolean = false;
  private mPiPReturnPending: boolean = false;
  private mPipEntryPending: boolean = false;
  /** Whether usePictureInPictureCoordinator intends to return the user to
   *  /player once the current PiP session ends (as opposed to closing
   *  playback or converting to Guide PiG). */
  private mReturnToPlayerIntended: boolean = false;
  /** Whether an active browser PiP session is being converted into a Guide
   *  PiG session, so the coordinator's leave handler must not treat it as a
   *  return-to-player or a close. */
  private mPipToGuideConversionActive: boolean = false;

  /** Whether GuideScreen is unmounting to navigate to the full-screen Player
   *  for the channel just tuned, so its cleanup must not stop the session
   *  that was just started via atmospheric(). */
  private mIsNavigatingToPlayer: boolean = false;

  /** Counts HTTP 401/409 DRM enforcement retries; reset on cleanPlayer(). */
  private mDrmEnforcementRetryCount: number = 0;

  // ── Constructor ──────────────────────────────────────────────────────────

  private constructor() {
    this.mAudioTracksMap = [];
    this.mDrmManager = DrmManager.getInstance();
    this.mMetadataManager = new MetadataManager();
    this.mPlaybackURLManager = new PlaybackURLManager();
    this.mRestrictionManager = new RestrictionManager();
    this.mSessionManager = new SessionManager();
    this.mTrackPrefereneceUtils = new TrackPreferenceUtils();
    this.mVideoManager = new VideoManager(new ShakaVideoPlayerImpl());
    this.mPlaybackSessionCoordinator = new PlaybackSessionCoordinator(
      () => this.mVideoManager.getStats(),
      () => this.mVideoManager.isClosedCaptionActive(),
      () => {
        const selectedTextTrack = this.mVideoManager.getSelectedTextTrack();
        // Shaka reports currentTime relative to the manifest's presentation
        // timeline, which for live content is often epoch-anchored — subtract
        // the seekable range start (same normalization as updateNdvrLastViewedPosition())
        // to get a real "position into content" value instead of a huge timestamp.
        // Clamped to 0: only read for playbackEnd, but a session that ends
        // (error/policy/network-lost) before the player actually advances past
        // the seek range start would otherwise still go negative here.
        const positionSec = Math.max(
          0,
          this.mVideoManager.getPlayerCurrentTime() -
            this.mVideoManager.getSeekableRange().start
        );
        const inHomeLocation = InHomeLocationModel.get();
        const isOnline = navigator.onLine;
        let regionMode = "Error";
        if (inHomeLocation !== null && inHomeLocation.inRegion !== null) {
          regionMode = inHomeLocation.inRegion ? "In" : "Out";
        }
        let connectionMode: string | undefined;
        if (!isOnline) {
          connectionMode = "Offline";
        } else if (inHomeLocation !== null && inHomeLocation.inHome !== null) {
          connectionMode = inHomeLocation.inHome ? "In" : "Out";
        }
        return {
          playerState: this.mVideoPlayerCurrentState,
          positionMs: positionSec * MILLISECONDS_IN_A_SECOND,
          bufferedInfo: this.mVideoManager.getBufferedInfo(),
          durationSec: this.mVideoManager.getDuration(),
          ccLanguage:
            selectedTextTrack !== null ? selectedTextTrack.language : undefined,
          connectionMode,
          drmType: this.getDrmServerType() || undefined,
          providerId: this.getCurrentPlayingproviderId(),
          regionMode,
          transportEncodingType: this.getTransportEncodingType() || undefined,
          variants: this.mVideoManager.getVideoVariants()
        };
      }
    );
  }

  public static getInstance(): VideoPlaybackController {
    if (VideoPlaybackController.mInstance === null) {
      VideoPlaybackController.mInstance = new VideoPlaybackController();
    }
    return VideoPlaybackController.mInstance;
  }

  // ── Player Configuration & Init ──────────────────────────────────────────

  /**
   * Attach the player to its DOM elements.
   *
   * @param videoEl - Element for main (Shaka) playback.
   * @param uiContainerRef - Container for UI overlays and view-mode classes.
   * @param standaloneEl - Optional element reserved for standalone media
   * (an EAS alert tone and similar). Must be a different element from
   * `videoEl`: Shaka binds its own DOM listeners and a MediaSource to that
   * one, so sharing it would both corrupt main playback state and make the
   * main stream unrecoverable without a re-tune.
   */
  public configurePlayer(
    videoEl: HTMLVideoElement,
    uiContainerRef: HTMLDivElement,
    standaloneEl?: HTMLVideoElement
  ): void {
    this.log("info", "configurePlayer()");
    this.mVideoManager.init(videoEl, uiContainerRef, standaloneEl);
    this.initPlayer();
  }

  private initPlayer(): void {
    this.log("info", "initPlayer()");

    // Bound once for the lifetime of this singleton. SubscriptionModel dedupes
    // by function reference, but every handler below is a fresh closure — an
    // arrow, or a new .bind() — so that dedupe can never match them. Without
    // this guard a second configurePlayer() (a remount of VideoPlayer, or an
    // HMR reload in development) leaves two of each handler subscribed
    // permanently, and one DRM SUCCESS then runs onDrmConfigured() twice,
    // creating two streaming sessions for a single tune.
    if (this.mArePlayerListenersBound) {
      this.log("info", "initPlayer() - listeners already bound, skipping");
      return;
    }
    this.mArePlayerListenersBound = true;

    // DrmManager listener
    this.mDrmManager
      .getDrmManagerListener()
      .subscribe((videoProviderState: VideoProviderState) => {
        this.getUpdatedDomainConfig(videoProviderState);
      });

    // MetadataManager listener
    this.mMetadataManager
      .getMetadataStateChangeListener()
      .subscribe(this.onMetadataStateChangeHandler.bind(this));

    // RestrictionManager listener
    this.mRestrictionManager
      .getRestrictionListener()
      .subscribe(this.onRestrictionHandler.bind(this));

    // SessionManager listener
    this.mSessionManager
      .getSessionStateChangeListener()
      .subscribe(this.onSessionStateChangeHandler.bind(this));

    // VideoManager listeners
    this.mVideoManager
      .getPlayerStateSubscription()
      .subscribe(this.onPlayerStateChangeHandler.bind(this));

    this.mVideoManager
      .getPlayerErrorSubscription()
      .subscribe(this.onPlaybackErrorHandler.bind(this));

    this.mVideoManager
      .getPlayerViewSubscription()
      .subscribe(this.onPlayerViewHandler.bind(this));

    this.mVideoManager
      .getFirstFrameSubscription()
      .subscribe(this.onFirstFrame.bind(this));

    this.mVideoManager
      .getInitiationSubscription()
      .subscribe(this.onPlayerInitiation.bind(this));

    this.mVideoManager
      .getStandalonePlaybackEventSubscription()
      .subscribe(this.onStandalonePlaybackEventHandler.bind(this));
  }

  // ── Ready & Lifecycle ────────────────────────────────────────────────────

  public async ready(): Promise<void> {
    this.log("info", "ready()");
    if (this.mIsReady) {
      return;
    }
    this.mIsReady = true;
    this.mount();
    this.mReadySubscription.notify(true);
    if (!this.mIsPlaying && this.mPlayable !== null) {
      this.mIsPlaying = true;
      this.play(this.mPlayable);
    } else if (DeviceModel.isTvDevice()) {
      const lastChannelNumber = await DeviceModel.getLastTunedChannelNumber();
      const initialPlayable: IPlayable =
        await new ChannelsInteractor().getLastPlayedOrFallbackToFirstByChannelNumberV2(
          lastChannelNumber || null
        );
      this.startPlayback(initialPlayable);
    }
  }

  public mount(): void {
    this.mVideoManager.mount();
  }

  public unmount(): void {
    this.clearAllLiveWatchJitterTimers();
    this.clean();
    this.mVideoManager.unmount();
  }

  public homeScreenVideoWindow(): void {
    this.mVideoManager.homeScreenVideoWindow();
  }

  // ── Playback Lifecycle ───────────────────────────────────────────────────

  public async startPlayback(
    playable: IPlayable,
    isForcedPlay: boolean = true
  ): Promise<void> {
    this.mPlayerError = null;

    this.log("info", "startPlayback() - " + playable.playbackContentType);

    this.log(
      "info",
      "startPlayback() channelNumber - " +
        (playable.channel !== undefined && playable.channel !== null
          ? playable.channel.channelNumber
          : "")
    );

    if (!this.mIsReady) {
      this.setPlayable(playable);
      return;
    }

    if (!this.mIsNetworkConnected) {
      this.onRestrictionHandler(PlaybackRestrictionType.NETWORK_DISCONNECTED);
      return;
    }

    // AppLinear channels play inside a partner app and this app does not
    // deep-link out, so there is nothing here to tune. Refused at the funnel
    // rather than only at the guide and channel-change entry points, because
    // channel-number entry, the smart carousel, switchToLiveTv and
    // resumeFromHomeScreen all reach playback without passing either.
    // Silent — an untunable channel is a gap in the lineup, not a failure to
    // report — where the entry points that can do better skip on to the next
    // tunable channel instead.
    if (
      playable.channel !== undefined &&
      !ChannelsInteractor.isTunableChannel(playable.channel)
    ) {
      this.log(
        "info",
        "startPlayback() - channel " +
          String(playable.channel.channelNumber) +
          " is not tunable by this app, ignoring"
      );
      return;
    }

    if (VideoPlaybackController.isUnsupportedForTivoPlus(playable)) {
      this.log(
        "error",
        "startPlayback() - " +
          String(playable.playbackContentType) +
          " requested for a TiVo+ channel, which supports live playback only"
      );
      this.mPlaybackErrorSubscription.notify(
        new PlaybackError(
          PlaybackSetupErrorCode.UNSUPPORTED_CONTENT,
          Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE)
        )
      );
      return;
    }

    switch (playable.playbackContentType) {
      case PlaybackContentType.LIVE:
        await this.startLivePlayback(playable, isForcedPlay);
        break;
      case PlaybackContentType.STARTOVER_BEGINNING:
      case PlaybackContentType.STARTOVER_RESUME:
      case PlaybackContentType.CATCHUP_BEGINNING:
      case PlaybackContentType.CATCHUP_RESUME:
        await this.startSoCuPlayback(playable);
        break;
      case PlaybackContentType.NDVR_LIVE:
      case PlaybackContentType.NDVR_RECORDED:
        await this.startNdvrPlayback(playable);
        break;
      case PlaybackContentType.VOD:
        await this.startVodPlayback(playable);
        break;
      default:
        break;
    }
  }

  /** Whether the channel currently playing is a TiVo+ channel. */
  private isTivoPlusPlayable(): boolean {
    return (
      this.mPlayable !== null &&
      this.mPlayable.channel !== undefined &&
      ChannelsInteractor.isTivoPlusChannel(this.mPlayable.channel)
    );
  }

  /**
   * Whether this request asks a TiVo+ channel for something it cannot do.
   *
   * TiVo+ is live-only: there is no recording to play back and no start-over
   * or catch-up window. The UI already withholds those entry points, so
   * reaching here means one was missed — refusing it keeps the failure loud
   * and near its cause instead of surfacing later as an unexplained playback
   * error.
   */
  private static isUnsupportedForTivoPlus(playable: IPlayable): boolean {
    if (playable.playbackContentType === PlaybackContentType.LIVE) {
      return false;
    }

    if (playable.channel === undefined) {
      return false;
    }

    return ChannelsInteractor.isTivoPlusChannel(playable.channel);
  }

  private async startLivePlayback(
    playable: IPlayable,
    isForcedPlay: boolean
  ): Promise<void> {
    try {
      if (
        !this.mIsPlaying ||
        this.mPlayable === null ||
        // A same-channel switch out of Start Over / Catch Up asks for LIVE with
        // the channel already playing. Only a stream that is already live can
        // be resumed in place; anything else has to re-tune, or the playable
        // keeps its old content type and the SOCU stream just carries on.
        !this.isLive() ||
        this.mPlayable.channel === undefined ||
        playable.channel === undefined ||
        this.mPlayable.channel.channelId !== playable.channel.channelId
      ) {
        // A tune that has not reached PLAYING yet is torn down too, or its
        // session and in-flight setup carry on underneath the new one.
        if (this.mIsPlaying || this.mIsTuneInProgress) {
          await this.stop();
        }
        this.setPlayable(playable);
        this.play(playable);
      } else if (isForcedPlay) {
        this.resume();
      }
    } catch (error: unknown) {
      this.log("error", "startLivePlayback()", error);
    }
  }

  private async startNdvrPlayback(playable: IPlayable): Promise<void> {
    try {
      if (this.mIsPlaying || this.mIsTuneInProgress) {
        await this.stop();
      }
      this.setPlayable(playable);
      this.play(playable);
    } catch (error: unknown) {
      this.log("error", "startNdvrPlayback()", error);
    }
  }

  private async startSoCuPlayback(playable: IPlayable): Promise<void> {
    try {
      if (this.mIsPlaying || this.mIsTuneInProgress) {
        await this.stop();
      }
      this.setPlayable(playable);
      this.play(playable);
    } catch (error: unknown) {
      this.log("error", "startSoCuPlayback()", error);
    }
  }

  private async startVodPlayback(playable: IPlayable): Promise<void> {
    try {
      const isDifferentVod =
        playable.vodPlaybackModel !== undefined &&
        this.mPlayable !== null &&
        this.mPlayable.vodPlaybackModel !== undefined &&
        this.mPlayable.vodPlaybackModel.id !== playable.vodPlaybackModel.id;

      if (
        (playable.vodPlaybackModel !== undefined &&
          playable.vodPlaybackModel.isStartOver === true) ||
        !this.mIsPlaying ||
        isDifferentVod
      ) {
        // A force-paused title is still loaded and still holds its session,
        // so it is torn down the same as a playing one.
        if (this.mIsPlaying || this.mIsForcedPaused || this.mIsTuneInProgress) {
          await this.stop();
        }
        this.setPlayable(playable);
        this.play(playable);
      }
    } catch (error: unknown) {
      this.log("error", "startVodPlayback()", error);
    }
  }

  private play(playable: IPlayable): void {
    this.log("info", "play()");

    // Clear any restriction verdict left over from the previous content
    // before this content's own check has a chance to run — setUpPlayer()'s
    // isPlaybackBlockedByRestriction() guard reads this field, and a stale
    // DEVICE_RESTRICTED/BLACKLISTED/etc. from whatever was playing before
    // must not block this new, potentially unrestricted, content.
    this.mRestrictionType = PlaybackRestrictionType.NO_RESTRICTION;
    this.mActiveSession = null;
    this.mTrickPlayEligibilitySession = null;

    // Check if metadata update is required
    if (this.isMetadataUpdateRequired()) {
      this.mMetadataManager.prepareMetadata(playable);
    }
    const sessionContext: TPlaybackSessionContext = {
      playable,
      metadata: this.mMetadataManager.getMetadata(),
      streamingUrl: null,
      contentType: playable.playbackContentType
    };
    this.mPlaybackSessionCoordinator.start(sessionContext);

    this.mTuneGeneration += 1;
    this.mIsTuneInProgress = true;
    const generation: number = this.mTuneGeneration;

    // Check restrictions before setting up player
    this.mRestrictionManager
      .checkCanPlayStartRestriction(playable)
      .then((restrictionType) => {
        if (generation !== this.mTuneGeneration) {
          this.log(
            "info",
            "play() - restriction result for a superseded tune, ignoring"
          );
          return;
        }
        this.onRestrictionHandler(restrictionType);
        if (this.isRestrictionAllowingPlaybackStart(restrictionType)) {
          this.continueStartPlayback(playable);
        }
      })
      .catch((error: unknown) => {
        this.log("error", "play() - restriction check failed", error);
      });
  }

  /**
   * Whether a restriction verdict lets playback setup go ahead: no
   * restriction at all, or an unreachable in-home service while the grace
   * period is still running.
   */
  private isRestrictionAllowingPlaybackStart(
    restrictionType: PlaybackRestrictionType
  ): boolean {
    return (
      restrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
      (!this.mIsGracePeriodFinished &&
        restrictionType === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE)
    );
  }

  /**
   * Whether the current metadata state is a blocking `*_ERROR` for the
   * playable in progress. DRM/session setup is async and can settle after a
   * blocking metadata error already fired its own `clean()` — every callback
   * that resumes playback setup must re-check this before proceeding, or it
   * undoes that `clean()` and plays behind the error dialog.
   */
  private isBlockedByMetadataError(): boolean {
    const metadataState = this.mMetadataManager.getMetadataState();
    return (
      metadataState.endsWith("_ERROR") &&
      this.isMetadataErrorBlocking(metadataState)
    );
  }

  private continueStartPlayback(playable: IPlayable): void {
    if (this.isBlockedByMetadataError()) {
      this.clean();
      return;
    }

    // Set up player based on content type
    if (this.isVod()) {
      // VOD sets its player up from onVodActionDetailsReady() instead, as
      // SOCU does: the provider to configure DRM for and the identifiers the
      // session is created against both come from the watch action, which is
      // still in flight here. Starting DRM now would race it, and a session
      // created before it lands has no partnerId to create against.
      this.log("info", "continueStartPlayback() - VOD awaits action details");
    } else if (this.isLive() && playable.channel !== undefined) {
      // TODO: Use channel.linearProviderPartnerId when Channel entity is extended
      this.setUpPlayer(playable, "");
    } else if (this.isSocu()) {
      // SOCU sets its player up from onActionDetailsReady() instead, once the
      // watch action names the provider. Nothing to do here — and specifically
      // not clean(), which races that path: both are async continuations of
      // play(), so a clean() landing after the pre-session load would tear
      // down the stream it had just started.
      this.log("info", "continueStartPlayback() - SOCU awaits action details");
    } else {
      this.clean();
    }
  }

  /**
   * Whether the last-known restriction result should block playback from
   * starting/continuing. Mirrors the unconditional-stop case group in
   * onRestrictionHandler() — the restrictions that mean "policy prevents
   * playing this content at all", as opposed to ADULT (mute-and-resume) or
   * the in-home/network restrictions, which have their own resume/grace-
   * period handling elsewhere.
   */
  private isPlaybackBlockedByRestriction(): boolean {
    return (
      this.mRestrictionType === PlaybackRestrictionType.BLACKLISTED ||
      this.mRestrictionType === PlaybackRestrictionType.OUT_OF_HOME ||
      this.mRestrictionType === PlaybackRestrictionType.UNSUBSCRIBED ||
      this.mRestrictionType === PlaybackRestrictionType.DEVICE_RESTRICTED
    );
  }

  public setUpPlayer(playable: IPlayable, partnerId: string): void {
    this.log("info", "setUpPlayer()");
    // SOCU (Start Over / Catch Up) reaches here from onActionDetailsReady(),
    // which — unlike the LIVE/VOD path in continueStartPlayback() — has no
    // restriction gate of its own: it fires off the ACTION_LIST_READY/
    // ACTION_DETAILS_READY metadata chain regardless of what
    // checkCanPlayStartRestriction() found. Without this guard, a device
    // restricted for SoCu content still starts audio/video, then only stops
    // once the (slower, metadata-dependent) checkAllRestriction() catches up.
    // Guarding centrally here also covers a stop() that already landed: the
    // restriction type it set stays blocking, so a late DRM/metadata
    // continuation that was already in flight can no longer resurrect
    // playback underneath the error overlay.
    if (this.isPlaybackBlockedByRestriction()) {
      this.log(
        "warn",
        "setUpPlayer() - blocked by restriction: " + this.mRestrictionType
      );
      return;
    }
    if (partnerId) {
      this.setupDrm(partnerId);
      return;
    }
    const providerId: string =
      playable.channel !== undefined && playable.channel.linearProviderPartnerId
        ? playable.channel.linearProviderPartnerId
        : partnerId;
    this.setupDrm(providerId);
  }

  private async setupDrm(
    providerId: string,
    isForcedDrm: boolean = false
  ): Promise<void> {
    this.log("info", "setupDrm() - " + providerId);
    // Armed before the request: its SUCCESS can be notified before it returns.
    this.mDrmRequestGeneration = this.mTuneGeneration;
    try {
      await this.mDrmManager.getDRMInfoForProviderId(providerId, isForcedDrm);
    } catch (error: unknown) {
      this.log("error", "setupDrm()", error);
    }
  }

  public async stop(): Promise<void> {
    this.cleanPlayer();
    this.mVideoManager.stop();
  }

  public clean(): void {
    this.cleanPlayer();
    this.mVideoManager.clean();
  }

  private cleanPlayer(): void {
    this.mPlaybackSessionCoordinator.onPlaybackStopped(
      SessionEndReason.USER_ENDED
    );
    this.trickPlayStop();
    this.mAudioTracksMap = [];
    this.mIsForcedPaused = false;
    this.mIsMetadataLoaded = false;
    this.mIsPlayerLoaded = false;
    this.mIsPlaying = false;
    // Whatever the stopped tune still has in flight now belongs to the past.
    this.mTuneGeneration += 1;
    this.mIsTuneInProgress = false;
    this.mDrmRequestGeneration = null;
    this.mTextTracks = [];
    this.mIsClosedCaptionHiddenForViewMode = false;
    this.mDrmEnforcementRetryCount = 0;
    this.mActiveSession = null;
    this.mTrickPlayEligibilitySession = null;
    this.mSessionManager.sessionStop();
    this.clearLastViewedPositionInterval();
    this.clearLiveResumeTimer();
    this.clearLiveWatchThresholdTimer();
    if (this.mSessionManagerGracePeriodTimeoutId !== null) {
      clearTimeout(this.mSessionManagerGracePeriodTimeoutId);
      this.mSessionManagerGracePeriodTimeoutId = null;
    }
    this.clearProgramBoundaryTimeout();
    // Placeholder warnings are suppressed after the first occurrence so a
    // template resolved on every tune does not flood the log. Clearing that
    // here scopes the suppression to one playback, so the same gap is
    // reported again on the next channel instead of being lost.
    TivoPlusUrlResolver.resetSessionLogState();
  }

  // ── Standalone Playback ──────────────────────────────────────────────────
  //
  // Standalone media is a progressive file (an EAS alert tone and similar)
  // played by a separate glue player on its own element, outside the Shaka
  // pipeline. It carries no session, metadata or restriction handling of its
  // own.
  //
  // The two playback paths are independent in both directions, except for one
  // deliberate coupling: startStandalonePlayback() mutes and pauses main
  // playback, because an emergency alert must interrupt whatever is playing
  // and take precedence over it. Per the EAS requirements this interruption
  // is mute + pause only — never a stop()/unload() — so main playback stays
  // loaded (session, metadata and trick-play state untouched) and can later
  // be resumed with a plain unmute + play(), with no re-tune required.
  // Nothing else crosses over — stopStandalonePlayback() leaves main
  // playback alone, and stop() / clean() / cleanPlayer() leave an in-flight
  // alert tone alone, so a tune cannot silence one.
  //
  // Unmuting/unpausing after the alert ends is deliberately not wired here —
  // that is a separate, later change.

  /**
   * Interrupt main playback and start standalone media playback.
   *
   * Mutes and force-pauses main playback rather than stopping it, so it can
   * later be resumed without a re-tune.
   *
   * @param url - The media URL to play. Blank URLs are ignored, and main
   * playback is left running.
   */
  public async startStandalonePlayback(url: string): Promise<void> {
    if (url.trim().length === 0) {
      this.log("warn", "startStandalonePlayback() - blank url, ignoring");
      return;
    }

    this.log("info", "startStandalonePlayback()");
    this.mVideoManager.mute(true);
    this.pause(true);
    this.mVideoManager.startStandalonePlayback(url);
  }

  /**
   * Stop standalone media playback.
   *
   * Reports no end of media, and does not resume main playback — what happens
   * next is the caller's decision.
   */
  public stopStandalonePlayback(): void {
    this.log("info", "stopStandalonePlayback()");
    this.mVideoManager.stopStandalonePlayback();
  }

  /**
   * Whether standalone media is currently playing.
   */
  public isStandalonePlaying(): boolean {
    return this.mVideoManager.isStandalonePlaying();
  }

  /**
   * Relay a standalone playback event to subscribers.
   *
   * Deliberately a pure relay: standalone playback has no session, metadata
   * or restriction state, so this handler must not touch any of it.
   *
   * @param event - The event raised by the standalone glue player
   */
  private onStandalonePlaybackEventHandler(
    event: TStandalonePlaybackEvent
  ): void {
    this.log("info", "onStandalonePlaybackEventHandler() - " + event.state);
    this.mStandalonePlaybackEventSubscription.notify(event);
  }

  public restorePlayback(): void {
    this.mPlayerError = null;
    if (this.mPlayable === null || this.mPlayable.channel === undefined) {
      return;
    }
    const playable = this.mPlayable;
    this.mRestrictionManager
      .checkCanPlayStartRestriction(playable)
      .then((playbackRestrictionType) => {
        if (
          playbackRestrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
          (!this.mIsGracePeriodFinished &&
            playbackRestrictionType ===
              PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE)
        ) {
          if (this.mPlayable !== null) {
            if (this.mIsPlayerLoaded) {
              this.prepareMetadataForPlayable();
            } else {
              this.startPlayback(this.mPlayable, false);
            }
          }
        } else {
          // No reload follows, so a position held for one would go stale —
          // and there is no refresh left to retry.
          this.mNetworkRestoreSeek = null;
          this.clearReconnectRefresh();
          this.onRestrictionHandler(playbackRestrictionType);
          this.log(
            "info",
            "restorePlayback() - restricted: " + playbackRestrictionType
          );
        }
      });
  }

  private getCurrentPlayingproviderId(): string | undefined {
    const drmInfo = this.mVideoManager.getDrmInfo();

    if (drmInfo && drmInfo.providerPartnerId) {
      return drmInfo.providerPartnerId;
    }

    if (this.isLive()) {
      return this.mPlayable && this.mPlayable.channel
        ? this.mPlayable.channel.linearProviderPartnerId
        : undefined;
    }

    if (this.isSocu()) {
      const metadata = this.getMetadata();
      return metadata && metadata.watchAction && metadata.watchAction.kernel
        ? metadata.watchAction.kernel.partnerId
        : undefined;
    }

    if (this.isNdvr()) {
      const watchAction = this.mMetadataManager.getMetadata().watchAction;
      return watchAction && watchAction.kernel
        ? (watchAction.kernel.recordingProviderPartnerId as string)
        : undefined;
    }

    if (this.isVod()) {
      if (this.mPlayable === null) {
        return undefined;
      }
      const vodProviderId = VodPlaybackUtils.getProviderId(
        this.mPlayable,
        this.getMetadata()
      );
      return vodProviderId !== "" ? vodProviderId : undefined;
    }

    return undefined;
  }

  // ── MetadataManager Listener ─────────────────────────────────────────────

  private onMetadataStateChangeHandler(state: MetadataState): void {
    this.log("info", "onMetadataStateChangeHandler() - " + state);

    if (state === MetadataState.AIRING_READY) {
      this.clearReconnectRefresh();
    }

    switch (state) {
      case MetadataState.AIRING_READY:
      case MetadataState.PREVIEW_READY:
      case MetadataState.PREVIEW_ERROR:
        this.mAudioTracksMap = [];
        this.onMetadataLoaded();
        this.scheduleProgramBoundary();
        break;
      case MetadataState.AIRING_ERROR:
        // Retrying keeps the stream that survived the outage; the viewer only
        // sees the error if every retry fails.
        if (this.retryReconnectRefresh()) {
          return;
        }
        this.clean();
        break;
      case MetadataState.RECORDING_ACTION_INIT_READY:
      case MetadataState.RECORDING_ACTION_READY:
        this.onRecordingActionReady();
        break;
      case MetadataState.VOD_PREVIEW_READY:
      case MetadataState.VOD_PREVIEW_ERROR:
        this.mAudioTracksMap = [];
        this.onMetadataLoaded();
        break;
      case MetadataState.ACTION_LIST_READY:
        this.onActionListReady();
        break;
      case MetadataState.ACTION_DETAILS_READY:
        this.onActionDetailsReady();
        break;
      case MetadataState.ACTION_LIST_ERROR:
      case MetadataState.ACTION_DETAILS_ERROR:
      case MetadataState.RECORDING_ACTION_ERROR:
        // Defer to the same blocking table the dialog gating uses, so a
        // content type marked blocking here can't show the dialog while
        // playback silently continues underneath it.
        if (this.isMetadataErrorBlocking(state)) {
          this.clean();
        }
        break;
      default:
        break;
    }

    this.mMetadataStateSubscription.notify(state);
  }

  private onMetadataLoaded(): void {
    this.log("debug", "onMetadataLoaded()");
    this.mIsMetadataLoaded = true;
    this.mPlaybackSessionCoordinator.updateSessionValues({
      metadata: this.mMetadataManager.getMetadata()
    });
    // Check restrictions with metadata
    if (this.mPlayable !== null) {
      const playable = this.mPlayable;
      const metadata = this.mMetadataManager.getMetadata();
      this.mRestrictionManager
        .checkAllRestriction(playable, metadata)
        .then((restrictionType) => {
          if (restrictionType !== PlaybackRestrictionType.NO_RESTRICTION) {
            this.onRestrictionHandler(restrictionType);
          }
        });
    }
  }

  private onRecordingActionReady(): void {
    this.log("debug", "onRecordingActionReady()");
    this.onMetadataLoaded();
    if (this.mPlayable !== null) {
      const watchKernel = getActionKernel(this.getMetadata().watchAction);
      const recordingProviderPartnerId: string =
        watchKernel !== undefined &&
        typeof watchKernel.recordingProviderPartnerId === "string"
          ? watchKernel.recordingProviderPartnerId
          : "";
      this.setUpPlayer(this.mPlayable, recordingProviderPartnerId);
    }
  }

  private onActionListReady(): void {
    this.log("debug", "onActionListReady()");
  }

  private onActionDetailsReady(): void {
    this.log("debug", "onActionDetailsReady()");
    if (this.isSocu() && this.mPlayable !== null) {
      const watchKernel = getActionKernel(this.getMetadata().watchAction);
      const soCuProviderID: string =
        watchKernel !== undefined && typeof watchKernel.partnerId === "string"
          ? watchKernel.partnerId
          : "";
      this.setUpPlayer(this.mPlayable, soCuProviderID);
    } else if (this.isVod()) {
      this.onVodActionDetailsReady();
    }
  }

  /**
   * Starts VOD playback setup once the watch action has resolved — the VOD
   * counterpart of SOCU's onActionDetailsReady() path.
   *
   * The metadata restrictions are checked first, against the watch action
   * just resolved, so a title restricted on this device never configures DRM
   * or opens a session. The start-up check in play() cannot do that: it runs
   * before the watch action exists.
   *
   * A preview the provider plays without a session goes straight to its
   * asset URL in the clear; everything else configures DRM for the watch
   * action's provider, and onDrmConfigured() creates the session.
   */
  private onVodActionDetailsReady(): void {
    const playable = this.mPlayable;
    if (playable === null) {
      return;
    }
    const metadata = this.getMetadata();

    this.mRestrictionManager
      .checkAllRestriction(playable, metadata)
      .then((restrictionType) => {
        // Another tune replaced this one while the check ran.
        if (this.mPlayable !== playable) {
          this.log(
            "info",
            "onVodActionDetailsReady() - playable changed, dropping"
          );
          return;
        }
        if (!this.isRestrictionAllowingPlaybackStart(restrictionType)) {
          // The start-up check in play() may already have raised this same
          // verdict; reporting it twice would stack a second overlay.
          if (restrictionType !== this.mRestrictionType) {
            this.onRestrictionHandler(restrictionType);
          }
          return;
        }
        if (this.isBlockedByMetadataError()) {
          this.clean();
          return;
        }

        if (VodPlaybackUtils.isSessionlessPreview(playable, metadata)) {
          this.log(
            "info",
            "onVodActionDetailsReady() - session-less preview, playing asset"
          );
          this.mVideoManager.clearDrm();
          this.initPlayback();
          return;
        }

        this.setUpPlayer(
          playable,
          VodPlaybackUtils.getProviderId(playable, metadata)
        );
      })
      .catch((error: unknown) => {
        this.log("error", "onVodActionDetailsReady()", error);
      });
  }

  private isMetadataUpdateRequired(): boolean {
    const { airingOffers } = this.getMetadata();

    // Check for the cases in LIVE playback
    if (
      this.mPlayable !== null &&
      this.mPlayable.playbackContentType === PlaybackContentType.LIVE
    ) {
      // If airingOffers is not available or empty or for adult restriction,
      // metadata update is required
      if (
        !airingOffers ||
        !airingOffers.length ||
        this.mRestrictionType === PlaybackRestrictionType.ADULT
      )
        return true;

      // Check if the channel has changed
      if (
        this.mPlayable.channel === undefined ||
        airingOffers[0].stationId !== this.mPlayable.channel.stationId
      )
        return true;

      // Check if current time is greater than end time of current program
      const currentTime =
        this.mVideoManager!.getPlayerCurrentTime() * MILLISECONDS_IN_A_SECOND ||
        new Date().getTime();
      const endDate = new AiringPreviewUtils(
        airingOffers,
        this.mMetadataManager.getMetadata().offerPreview
      ).getEndTime();
      let programEndTime = endDate.getTime();
      if (isNaN(programEndTime)) {
        programEndTime = Date.now();
      }
      return currentTime > programEndTime;
    }
    // Check if playback content type is changed to SOCU OR NDVR OR VOD
    return this.isSocu() || this.isNdvr() || this.isVod();
  }

  private prepareMetadataForPlayable(): void {
    if (this.mPlayable !== null) {
      this.mMetadataManager.prepareMetadata(this.mPlayable);
    }
  }

  private clearProgramBoundaryTimeout(): void {
    if (this.mProgramBoundaryTimeoutId !== null) {
      clearTimeout(this.mProgramBoundaryTimeoutId);
      this.mProgramBoundaryTimeoutId = null;
      this.log("debug", "clearProgramBoundaryTimeout");
    }
  }

  private scheduleProgramBoundary(): void {
    this.clearProgramBoundaryTimeout();

    const metadata = this.mMetadataManager.getMetadata();
    const airingOffers = metadata.airingOffers;

    if (
      airingOffers === undefined ||
      airingOffers === null ||
      airingOffers.length === 0
    ) {
      return;
    }

    const endDate = new AiringPreviewUtils(
      airingOffers,
      metadata.offerPreview
    ).getEndTime();
    let programEndTime = endDate.getTime();
    if (isNaN(programEndTime)) {
      // Safety net: fire on next tick so the handler revalidates against the
      // current playhead and either reschedules or notifies.
      programEndTime = Date.now();
    }

    const delta = Math.max(0, programEndTime - Date.now());
    const retryDelay = delta > 0 ? delta : PROGRAM_BOUNDARY_RETRY_DELAY_MS;

    this.log(
      "debug",
      "scheduleProgramBoundary() - delta=" + delta + " retryDelay=" + retryDelay
    );

    this.mProgramBoundaryTimeoutId = setTimeout(() => {
      this.onProgramBoundaryHandler(programEndTime);
    }, retryDelay);
  }

  private onProgramBoundaryHandler(programEndTime: number): void {
    this.mProgramBoundaryTimeoutId = null;

    const currentPlayheadMs = this.mVideoManager.getPlayerCurrentTime() * 1000;
    if (currentPlayheadMs < programEndTime) {
      // Drift / paused / trickplay: the playhead has not yet crossed the
      // boundary. Re-arm using the same deadline, but fall back to a short
      // retry interval once the boundary is already in the past.
      this.log(
        "debug",
        "onProgramBoundaryHandler() - early fire, rescheduling"
      );
      this.scheduleProgramBoundary();
      return;
    }

    this.log("info", "onProgramBoundaryHandler() - fired");

    if (this.mPlayable !== null) {
      this.mMetadataManager.prepareMetadata(this.mPlayable);
      const nextContext: TPlaybackSessionContext = {
        playable: this.mPlayable,
        metadata: this.mMetadataManager.getMetadata(),
        streamingUrl: this.mVideoManager.getStreamingUrl(),
        contentType: this.mPlayable.playbackContentType
      };
      this.mPlaybackSessionCoordinator.onProgramBoundary(nextContext);
    }
    this.mProgramBoundarySubscription.notify(undefined);
  }

  // ── RestrictionManager Listener ──────────────────────────────────────────

  private onRestrictionHandler(type: PlaybackRestrictionType): void {
    this.mRestrictionType = type;
    this.mPlaybackRestrictionTypeSubscription.notify(type);
    this.log("info", "onRestrictionHandler() - " + type);
    switch (type) {
      case PlaybackRestrictionType.ADULT:
        this.onAdultRestrictions();
        break;
      case PlaybackRestrictionType.BLACKLISTED:
      case PlaybackRestrictionType.OUT_OF_HOME:
      case PlaybackRestrictionType.UNSUBSCRIBED:
      case PlaybackRestrictionType.DEVICE_RESTRICTED:
        this.mPlaybackSessionCoordinator.onPlaybackStopped(
          SessionEndReason.POLICY_BLOCK
        );
        this.stop();
        break;
      case PlaybackRestrictionType.INHOME_LOCATION_ERROR:
        this.onInHomeError();
        break;
      case PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE:
        this.onInHomeUnreachable();
        break;
      case PlaybackRestrictionType.NETWORK_CONNECTED:
        this.onNetworkConnected();
        break;
      case PlaybackRestrictionType.NETWORK_DISCONNECTED:
        this.onNetworkDisconnected();
        break;
      case PlaybackRestrictionType.APP_BACKGROUND:
        this.onAppBackground();
        break;
      case PlaybackRestrictionType.APP_FOREGROUND:
        this.onAppForeground();
        break;
      case PlaybackRestrictionType.NO_RESTRICTION:
        this.onNoRestrictions();
        break;
      case PlaybackRestrictionType.OUT_OF_REGION:
        this.onOutOfHome();
        break;
      default:
        break;
    }
  }

  private onAppBackground(): void {
    // Capture the wall-clock time so the elapsed background duration can be
    // measured reliably on return — background timers are throttled/suspended
    // and cannot be relied upon to fire while hidden.
    this.mBackgroundedAtMs = Date.now();
    this.log(
      "info",
      "onAppBackground() - app moved to background" +
        VideoPlaybackController.LOG_IS_FORCED_PAUSED +
        String(this.mIsForcedPaused) +
        VideoPlaybackController.LOG_IS_PLAYER_LOADED +
        String(this.mIsPlayerLoaded)
    );
    // Auto-pause when backgrounded, unless the user has already explicitly
    // (forced) paused — mirrors onNetworkDisconnected().
    if (!this.mIsForcedPaused) {
      this.pause();
    }
  }

  private onAppForeground(): void {
    const backgroundedAtMs = this.mBackgroundedAtMs;
    this.mBackgroundedAtMs = null;
    const elapsedMs =
      backgroundedAtMs !== null ? Date.now() - backgroundedAtMs : 0;
    this.log(
      "info",
      "onAppForeground() - app returned to foreground" +
        ", backgroundMs=" +
        String(elapsedMs) +
        VideoPlaybackController.LOG_IS_FORCED_PAUSED +
        String(this.mIsForcedPaused) +
        VideoPlaybackController.LOG_IS_PLAYER_LOADED +
        String(this.mIsPlayerLoaded)
    );

    // If the app was backgrounded longer than the allowed window, the playback
    // session/stream is stale — re-sync instead of a plain resume.
    if (backgroundedAtMs !== null && elapsedMs >= APP_BACKGROUND_MAX_MS) {
      this.onReturnFromLongBackground(elapsedMs);
      return;
    }

    // Resume only if we auto-paused (not a user-forced pause) and a player
    // is loaded — mirrors onNoRestrictions().
    if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
      this.resume();
    }
  }

  private onReturnFromLongBackground(elapsedMs: number): void {
    this.log(
      "info",
      "onReturnFromLongBackground() - app was backgrounded for " +
        String(Math.round(elapsedMs / MILLISECONDS_IN_A_SECOND)) +
        "s (>= APP_BACKGROUND_MAX_MS) - re-syncing playback" +
        ", viewMode=" +
        String(this.mPlayerViewMode) +
        ", contentType=" +
        String(
          this.mPlayable !== null ? this.mPlayable.playbackContentType : null
        )
    );

    // Nothing was playing — nothing to re-sync. (A cold start after the OS
    // kills a backgrounded app is handled separately by app launch, which
    // opens the first screen.)
    if (this.mPlayable === null) {
      return;
    }

    // Hidden → no surface to re-sync into. Tear down the stale stream and keep
    // the deferred resume intent; restoreFromHidden() re-acquires the source
    // when a screen with a player is next opened.
    if (this.mPlayerViewMode === PlayerViewMode.Hidden) {
      this.log(
        "info",
        "onReturnFromLongBackground() - hidden view - dropping stale stream"
      );
      this.stop();
      return;
    }

    // Picture-in-Picture or any other non-full-screen view → switch to Live TV
    // on the last-viewed channel, regardless of content type.
    if (this.mPlayerViewMode !== PlayerViewMode.FullScreen) {
      this.log(
        "info",
        "onReturnFromLongBackground() - non-full-screen view - switching to Live TV"
      );
      this.switchToLiveTv();
      return;
    }

    // Full-screen Live TV / Start Over → move to the live point. After a long
    // background the buffered seek range is stale, so re-tune to true live.
    if (this.isLive() || this.isStartOver()) {
      this.log(
        "info",
        "onReturnFromLongBackground() - Live/StartOver - moving to live point"
      );
      this.switchToLiveTv();
      return;
    }

    // Full-screen Catchup / nDVR / VOD → tear down stale playback and return to
    // the previous screen (navigation is performed by the view layer).
    this.log(
      "info",
      "onReturnFromLongBackground() - Catchup/nDVR/VOD - returning to previous screen"
    );
    this.stop();
    this.mExitToPreviousScreenSubscription.notify();
  }

  private onAdultRestrictions(): void {
    if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
      this.mVideoManager.mute(true);
      this.resume();
    } else if (this.mPlayable !== null && this.isLive()) {
      // TODO: Use channel.linearProviderPartnerId when Channel entity is extended
      this.setUpPlayer(this.mPlayable, "");
    }
  }

  public mute(): void {
    this.mVideoManager.mute(true);
  }

  public unmute(): void {
    this.mVideoManager.mute(false);
  }

  public isMuted(): boolean {
    return this.mVideoManager.isMuted();
  }

  public setVolume(volume: number): void {
    this.mVideoManager.getPlayer().setVolume(volume);
  }

  public getVolume(): number {
    return this.mVideoManager.getPlayer().getVolume();
  }

  private onInHomeError(): void {
    if (this.isPlaying()) this.stop();
    this.clearInHomeGracePeriodParameters();
  }

  private onInHomeUnreachable(): void {
    this.startInHomeGracePeriod();
    if (this.mIsGracePeriodFinished) {
      if (this.isPlaying()) this.stop();
    } else {
      this.resume();
    }
  }

  private onOutOfHome(): void {
    if (this.isPlaying()) this.stop();
    this.clearInHomeGracePeriodParameters();
  }

  private onNetworkConnected(): void {
    this.mIsNetworkConnected = true;
    this.mVideoManager.setSurfaceBlanked(false);
    if (this.mIsAntiburnActive || this.mIsForcedPaused) {
      // Neither branch reloads the stream here, so a position held for that
      // reload would only linger and could be applied to a later, unrelated
      // one (a player retry after the viewer has moved on).
      this.mNetworkRestoreSeek = null;
    }
    if (this.mIsAntiburnActive) {
      return;
    }
    if (!this.mIsForcedPaused) {
      this.clearReconnectRefresh();
      if (this.mPlayable !== null) {
        this.mReconnectRefresh = { playable: this.mPlayable, attempt: 0 };
      }
      this.restorePlayback();
    }
    // Re-enable trick-play from the live session now that we are back online —
    // restores the exact eligibility (seek range, Start Over / Go LIVE, etc.)
    // that applied before the disconnect. mIsNetworkConnected is true here, so
    // this re-derives the real (non-disabled) state.
    this.applyTrickPlayRestrictionsFromSession(
      this.mSessionManager.getSession()
    );
    // Resume from the position where playback was paused on disconnect. Mirrors
    // kaveri's onNetworkConnected() resume for its VideoWindow mode — the
    // actively-watched player. In unity that is FullScreen (and the smaller
    // PIP / PIG watch modes); Atmospheric (background) and BrowserFullScreen
    // are intentionally excluded.
    if (
      this.mIsPlayerLoaded &&
      !this.mIsForcedPaused &&
      (this.mPlayerViewMode === PlayerViewMode.FullScreen ||
        this.mPlayerViewMode === PlayerViewMode.PIP ||
        this.mPlayerViewMode === PlayerViewMode.PIG)
    ) {
      this.resume();
    }
  }

  private onNetworkDisconnected(): void {
    this.mIsNetworkConnected = false;
    // The next reconnect starts a fresh refresh with a full retry budget.
    this.clearReconnectRefresh();
    this.mPlaybackSessionCoordinator.onPlaybackStopped(
      SessionEndReason.NETWORK_LOST
    );
    // Dismiss any in-progress trick-play as if the operation had completed:
    // stop the session at the current player position (no seek back) and
    // clear the speed / direction / skip indicators. onNetworkConnected()
    // then resumes from this same position once connectivity returns.
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null && tpm.isActive()) {
      this.trickPlayStop();
    }
    this.captureNetworkRestoreSeek();
    // Disable every trick-play control and the progress bar while offline.
    // mIsNetworkConnected is already false, so this applies DISABLED_ELIGIBILITY.
    this.applyTrickPlayRestrictionsFromSession(
      this.mSessionManager.getSession()
    );
    if (!this.mIsForcedPaused) {
      this.pause();
    }
    // The paused element keeps its last decoded frame. The no-network OSD's
    // black backdrop hides it on the player, but leaving the player (BACK to
    // Home's background video or the Guide window) would expose it frozen.
    // Blank the picture instead of unloading, so reconnect can still resume.
    this.mVideoManager.setSurfaceBlanked(true);
  }

  /**
   * Remember where SOCU playback was when the network dropped, so the reload
   * that onNetworkConnected() → restorePlayback() triggers can return there.
   *
   * Kept when one is already held for the same playable: a connection that
   * flaps before the reload reaches its first frame would otherwise overwrite
   * the real position with the freshly reloaded stream's zero.
   */
  private captureNetworkRestoreSeek(): void {
    const playable = this.mPlayable;
    if (playable === null || !this.isSocu() || !this.mIsPlayerLoaded) {
      this.mNetworkRestoreSeek = null;
      return;
    }
    if (
      this.mNetworkRestoreSeek !== null &&
      this.mNetworkRestoreSeek.playable === playable &&
      !this.mNetworkRestoreSeek.applied
    ) {
      return;
    }
    const offsetSeconds =
      this.mVideoManager.getPlayerCurrentTime() -
      this.mVideoManager.getSeekableRange().start;
    this.mNetworkRestoreSeek =
      Number.isFinite(offsetSeconds) && offsetSeconds > 0
        ? { playable, offsetSeconds, applied: false }
        : null;
    this.log(
      "info",
      "captureNetworkRestoreSeek() - offsetSeconds=" +
        (this.mNetworkRestoreSeek !== null ? String(offsetSeconds) : "none")
    );
  }

  /**
   * The held network-restore position, if it belongs to the playable now on
   * screen, marked as applied. One held for anything else is dropped.
   *
   * @returns Seconds from the start of the seekable range, or `null`.
   */
  private takeNetworkRestoreOffset(): number | null {
    const pending = this.mNetworkRestoreSeek;
    if (pending === null) {
      return null;
    }
    if (pending.playable !== this.mPlayable || !this.isSocu()) {
      this.mNetworkRestoreSeek = null;
      return null;
    }
    pending.applied = true;
    return pending.offsetSeconds;
  }

  /**
   * Drop the network-restore position once the reload it was applied to is
   * playing: it is meant for that one reload and must not be replayed on a
   * later one (a player retry after the viewer has moved on).
   */
  private releaseAppliedNetworkRestoreSeek(): void {
    if (this.mNetworkRestoreSeek !== null && this.mNetworkRestoreSeek.applied) {
      this.mNetworkRestoreSeek = null;
    }
  }

  /**
   * Schedule another attempt at the reconnect refresh after it failed, instead
   * of letting the failure tear down playback.
   *
   * @returns true when a retry was scheduled — the caller must leave playback
   * alone; false when the failure is not a reconnect refresh's or the retry
   * budget is spent, and it should be handled as usual.
   */
  private retryReconnectRefresh(): boolean {
    const pending = this.mReconnectRefresh;
    if (
      pending === null ||
      pending.playable !== this.mPlayable ||
      !this.mIsNetworkConnected
    ) {
      this.clearReconnectRefresh();
      return false;
    }
    if (pending.attempt >= MAX_RECONNECT_REFRESH_RETRY_COUNT) {
      this.log(
        "warn",
        "retryReconnectRefresh() - retries exhausted after " +
          String(pending.attempt) +
          " attempts"
      );
      this.clearReconnectRefresh();
      return false;
    }

    const delayMs =
      RECONNECT_REFRESH_RETRY_BASE_DELAY_MS * 2 ** pending.attempt;
    pending.attempt += 1;
    this.log(
      "warn",
      "retryReconnectRefresh() - refresh after reconnect failed, retry " +
        String(pending.attempt) +
        "/" +
        String(MAX_RECONNECT_REFRESH_RETRY_COUNT) +
        " in " +
        String(delayMs) +
        "ms"
    );
    if (this.mReconnectRefreshTimerId !== null) {
      clearTimeout(this.mReconnectRefreshTimerId);
    }
    this.mReconnectRefreshTimerId = setTimeout(() => {
      this.mReconnectRefreshTimerId = null;
      // The viewer may have tuned elsewhere while the retry was pending.
      if (
        this.mReconnectRefresh !== pending ||
        pending.playable !== this.mPlayable
      ) {
        return;
      }
      this.restorePlayback();
    }, delayMs);
    return true;
  }

  private clearReconnectRefresh(): void {
    this.mReconnectRefresh = null;
    if (this.mReconnectRefreshTimerId !== null) {
      clearTimeout(this.mReconnectRefreshTimerId);
      this.mReconnectRefreshTimerId = null;
    }
  }

  private onNoRestrictions(): void {
    if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
      this.resume();
    }
    this.clearInHomeGracePeriodParameters();
  }

  private startInHomeGracePeriod(): void {
    this.mIsGracePeriodFinished = false;
    const consumedPeriod =
      GracePeriodUtils.getInstance().getConsumedGracePeriod();
    if (consumedPeriod < IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD) {
      if (!this.mGracePeriodTimerId) {
        const startTime = Date.now();
        this.mGracePeriodTimerId = setTimeout(() => {
          GracePeriodUtils.getInstance().setConsumedGracePeriod(
            Date.now() - startTime
          );
          this.mGracePeriodTimerId = null;
          this.mIsGracePeriodFinished = true;
          this.onRestrictionHandler(
            PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE
          );
        }, IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD);
      }
    } else {
      this.mIsGracePeriodFinished = true;
    }
  }

  private clearInHomeGracePeriodParameters(): void {
    const consumedPeriod =
      GracePeriodUtils.getInstance().getConsumedGracePeriod();
    this.mIsGracePeriodFinished = false;
    if (consumedPeriod)
      GracePeriodUtils.getInstance().setConsumedGracePeriod(0);
    if (this.mGracePeriodTimerId) {
      clearTimeout(this.mGracePeriodTimerId);
      this.mGracePeriodTimerId = null;
    }
  }

  // ── SessionManager Listener ──────────────────────────────────────────────

  private onSessionStateChangeHandler(state: SessionState): void {
    this.log("info", "onSessionStateChangeHandler() - " + state);

    if (this.mSessionManagerGracePeriodTimeoutId !== null) {
      clearTimeout(this.mSessionManagerGracePeriodTimeoutId);
      this.mSessionManagerGracePeriodTimeoutId = null;
    }

    switch (state) {
      case SessionState.SUCCESS:
        this.onSessionSuccess();
        break;
      case SessionState.ERROR:
        this.onSessionError();
        break;
      case SessionState.FAILED_OPEN:
        this.onSessionFailedOpen();
        break;
      case SessionState.UNREACHABLE:
        this.handleGracePeriod();
        break;
      default:
        break;
    }

    this.mSessionStateSubscription.notify(state);
  }

  private onSessionSuccess(): void {
    this.log("info", "onSessionSuccess()");
    if (this.isBlockedByMetadataError()) {
      this.clean();
      return;
    }
    const session = this.mSessionManager.getSession();
    this.mActiveSession = session;
    this.mPlaybackSessionCoordinator.updateSessionValues({
      locality: session.localityId
    });
    this.applyTrickPlayRestrictionsFromSession(session);
    const drmConfig = this.mDrmManager.getDRMConfig();
    if (this.mPlayable !== null) {
      const sessionUrl = this.mPlaybackURLManager.getPlaybackUrl(
        this.mPlayable,
        this.mMetadataManager.getMetadata(),
        drmConfig,
        session
      );
      if (sessionUrl !== "") {
        this.mPlaybackSessionCoordinator.updateSessionValues({
          streamingUrl: sessionUrl
        });
        // The session URL is authoritative, but LIVE and SOCU may already be
        // playing the pre-session tuning URL (started in onDrmConfigured), so
        // it is applied only when it addresses a different stream — a reload
        // that changes nothing would restart playback for no reason.
        //
        // Compared for equivalence rather than equality: the two sources list
        // the same packager parameters in their own order, so `drm=…,group=…`
        // and `group=…,drm=…` are one stream spelled two ways and must not
        // trigger a reload. See PlaybackUrlUtils.areEquivalent().
        if (
          !PlaybackUrlUtils.areEquivalent(
            sessionUrl,
            this.mVideoManager.getStreamingUrl()
          )
        ) {
          this.playUri(sessionUrl, this.getStartPosition());
        }
      }
    }
  }

  /**
   * Map `session.trickModeRestrictions` + `session.startOverAvailable`
   * to an `ITrickPlayEligibility` and apply it to the TrickPlayManager.
   *
   * On a valid restrictions payload the manager is enabled and the
   * derived eligibility is applied. If restrictions are missing,
   * malformed, or unreachable, the manager is disabled per PRD §7.1
   * (TP-AP-1..4) — UI then renders the seek bar non-interactive and
   * disables Start Over / Go LIVE.
   *
   * @param session - The session entity returned by SessionManager.
   */
  private applyTrickPlayRestrictionsFromSession(session: Session): void {
    this.mTrickPlayEligibilitySession = session;
    this.mHasSeekableWindow = this.computeHasSeekableWindow();

    // While the network is disconnected, trick-play is fully disabled
    // regardless of session data — every enable path funnels through here
    // (session success, grace period, setStartOverAvailable from the poll),
    // so guarding once keeps all trick-play controls and the progress bar
    // disabled offline. The real eligibility is restored from the live
    // session by onNetworkConnected() once connectivity returns.
    if (!this.mIsNetworkConnected) {
      this.setTrickPlayEligibility(DISABLED_ELIGIBILITY);
      this.setTrickPlayRestrictionEnabled(false);
      this.pushStartOverLowerBound();
      return;
    }

    // TiVo+ is live-only: there is no timeshift buffer to move around in, so
    // every trick-play action is refused whatever the session reports. Placed
    // beside the offline gate because both are absolute — they override the
    // session rather than being derived from it — and because every path that
    // enables trick-play funnels through here, so one check covers them all.
    // The position indicator is unaffected: `trickPlayAllowed: false` blocks
    // the operations, it does not hide the seek bar.
    if (this.isTivoPlusPlayable()) {
      this.log(
        "debug",
        "applyTrickPlayRestrictionsFromSession() TiVo+ channel — trick-play disabled"
      );
      this.setTrickPlayEligibility(DISABLED_ELIGIBILITY);
      this.setTrickPlayRestrictionEnabled(true);
      return;
    }

    const restrictions = parseTrickModeRestrictions(
      session.trickModeRestrictions as Record<string, unknown>
    );
    const startOverAvailable: boolean = this.mStartOverAvailable;
    if (restrictions === null) {
      // Session responded successfully but has no valid restriction data
      // (empty / missing fields). This is NOT the same as an unreachable
      // API (TP-AP-4) — it simply means all restrictions apply. Keep the
      // permissive boot-time eligibility and enable the restriction flag
      // so that guardEligibility() passes.
      this.log(
        "info",
        "applyTrickPlayRestrictionsFromSession() no restrictions in session — keeping permissive defaults"
      );
    }

    // Use the active trick-play config from TrickPlayManager if init() has
    // already run; otherwise fall back to the default config.
    const tpm = this.mVideoManager.getTrickPlayManager();
    const config: ITrickPlayConfig = {
      ...(tpm !== null ? tpm.getConfig() : DEFAULT_TRICKPLAY_CONFIG),
      enableThumbnailSeek: this.isThumbnailModeEnabled()
    };
    const isSocuPlaying: boolean = this.isSocu();
    const isNdvrPlaying: boolean = this.isNdvr();
    const isVodPlaying: boolean = this.isVod();
    const eligibility: ITrickPlayEligibility = deriveEligibility(
      {
        startOverAvailable,
        isSocuPlaying,
        isNdvrPlaying,
        isVodPlaying,
        hasSeekableWindow: this.mHasSeekableWindow,
        trickModeRestrictions: restrictions
      },
      config
    );

    this.log(
      "info",
      `applyTrickPlayRestrictionsFromSession() restrictionsValid=true startOverAvailable=${startOverAvailable} isSocuPlaying=${isSocuPlaying} hasSeekableWindow=${this.mHasSeekableWindow}`
    );

    if (tpm === null) {
      // Content-type switches that share a session (e.g. Live → Start Over
      // via switchPlaybackContentType()) route through startPlayback(),
      // which calls stop() first.
      this.mVideoManager.initTrickPlay(config, eligibility);
    } else {
      this.setTrickPlayEligibility(eligibility);
    }
    this.setTrickPlayRestrictionEnabled(true);
    this.pushStartOverLowerBound();
  }

  /**
   * Whether the player's seekable range is wide enough to seek in — for live,
   * a timeshift buffer wider than MIN_TIMESHIFT_WINDOW_SECONDS (anything
   * narrower is just the manifest's live window); otherwise any non-empty
   * range. Empty before the stream loads.
   */
  private computeHasSeekableWindow(): boolean {
    const range = this.getSeekRange();
    const span = range.end - range.start;
    if (!Number.isFinite(span) || span <= 0) {
      return false;
    }
    return this.isLive() ? span > MIN_TIMESHIFT_WINDOW_SECONDS : true;
  }

  /**
   * Re-derive trick-play eligibility when the player's seekable window has
   * appeared or gone since it was last derived. Eligibility is first applied
   * at session time, before the stream has loaded and while the seekable
   * range is still empty, so the TMIS seek-bar range (none vs. timeshift /
   * full) is only settled once the player reports a range. Called on first
   * frame and from the playback poll; a no-op unless the window changed.
   *
   * TMIS only — IPTV eligibility does not depend on the window. Skipped until
   * a trick-play manager exists, since re-applying would otherwise create one.
   */
  public refreshSeekableWindowEligibility(): void {
    if (
      !this.isThumbnailModeEnabled() ||
      this.mTrickPlayEligibilitySession === null ||
      this.mVideoManager.getTrickPlayManager() === null ||
      this.computeHasSeekableWindow() === this.mHasSeekableWindow
    ) {
      return;
    }
    this.applyTrickPlayRestrictionsFromSession(
      this.mTrickPlayEligibilitySession
    );
  }

  private onSessionError(): void {
    this.log("error", "onSessionError()");
    const error = this.mSessionManager.getSessionError();
    this.mPlayerError = error.data !== undefined ? error.data : null;
    if (this.mPlayerError !== null) {
      this.mPlaybackErrorSubscription.notify(this.mPlayerError);
    }
    this.applyTrickPlayRestrictionsFromSession({} as Session);
    this.clean();
  }

  /**
   * Handles a session failure that playback is allowed to survive.
   *
   * Deliberately the near-opposite of {@link onSessionError}: the failure is
   * recorded and logged, but nothing is shown to the viewer and the video is
   * left playing on the URL already resolved. Trick play is disabled, since
   * the session that would have granted it never arrived — the same
   * all-restricted default an absent session produces anywhere else.
   */
  private onSessionFailedOpen(): void {
    this.log(
      "warn",
      "onSessionFailedOpen() - session failed; playback continues"
    );
    // Deliberately not stored in mPlayerError. That field means "playback is
    // in an error state" and gates the live-resume timer and the public
    // getPlayerError(); setting it would quietly degrade the playback this
    // path exists to protect. The failure stays readable on the session.
    this.applyTrickPlayRestrictionsFromSession({} as Session);
    // Matches the offline branch: whenever trick play drops to the
    // all-restricted default, the start-over lower bound is republished so it
    // does not describe a window that is no longer reachable.
    this.pushStartOverLowerBound();
  }

  private handleGracePeriod(): void {
    this.applyTrickPlayRestrictionsFromSession({} as Session);
    this.mSessionManagerGracePeriodTimeoutId = setTimeout(() => {
      const message = Branding.applyCustomerSupportPlaceholders(
        Locale.getString(LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_MESSAGE)
      );
      this.mSessionManager.setSessionError(
        PlaybackSetupErrorCode.GRACE_PERIOD_ENDED,
        message,
        Locale.getString(LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_TITLE)
      );
      this.mSessionManager.setSessionState(SessionState.ERROR);
      this.onSessionStateChangeHandler(SessionState.ERROR);
    }, SESSION_MANAGER_GRACE_PERIOD);
  }

  // ── DRM / VideoProvider Listener ─────────────────────────────────────────

  private onDrmConfigured(): void {
    this.log("info", "onDrmConfigured()");
    if (this.mPlayable === null) {
      return;
    }
    if (this.isBlockedByMetadataError()) {
      this.clean();
      return;
    }

    const drmType = this.mVideoManager.getDrmServerType() || "";
    const transportEncodingType =
      this.mVideoManager.getTransportEncodingType() || "";

    const sessionCreateModel: ISessionCreateModel = {
      drmType: drmType,
      transportEncodingType: transportEncodingType,
      playbackContentType: this.mPlayable.playbackContentType,
      channel: this.mPlayable.channel,
      watchAction: this.mMetadataManager.getMetadata().watchAction,
      recordingWatchAction: undefined,
      vodWatchAction: undefined
    };

    if (this.isNdvr()) {
      sessionCreateModel.recordingWatchAction =
        this.mMetadataManager.getMetadata().watchAction;
    } else if (this.isVod()) {
      sessionCreateModel.vodWatchAction =
        this.mMetadataManager.getMetadata().watchVodAction;
      sessionCreateModel.isPreview = this.isVodPreview();
    }

    // LIVE and SOCU resolve their URL from the channel's own tuning entry and
    // start playing FIRST; the session is created afterwards, against a stream
    // that is already on screen. SOCU joins LIVE in this because its `socu://`
    // entry addresses the airing window directly, so there is nothing to wait
    // for. A channel that delivers no usable entry yields no pre-session URL
    // and simply plays when the session responds.
    //
    // Ordered this way deliberately: the session response is what carries the
    // authoritative URL, and resolving ours before asking for it means the two
    // can be compared in onSessionSuccess() and the reload skipped when they
    // address the same stream.
    //
    // A resumed catch-up airing opens at its saved position; everything else
    // here starts where the stream does.
    if (this.isLive() || this.isSocu()) {
      this.initPlayback(this.getStartPosition());
    }

    this.mSessionManager.createSession(sessionCreateModel);
  }

  private async onDrmFail(): Promise<void> {
    this.log("error", "onDrmFail()");
    const errorCount = await this.mDrmManager.getDRMErrorCount();
    if (errorCount < MAX_DRM_ERROR_RETRY_COUNT && this.mPlayable !== null) {
      // Retry DRM fetch
      const providerId: string = this.getCurrentPlayingproviderId() || "";
      this.setupDrm(providerId, true);
    } else if (this.mVideoPlayerCurrentState !== PlayerState.PLAYING) {
      this.log("error", "onDrmFail() - max retries exceeded");
      const latestDomainConfig = this.mDrmManager.getDRMConfig();
      const errorCode: string =
        Object.keys(latestDomainConfig).length === 0
          ? PlaybackSetupErrorCode.VP_CONFIGURATIONS_EMPTY_RESPONSE
          : PlaybackSetupErrorCode.VP_CONFIGURATIONS_MISCONFIGURATION;
      const error: PlaybackError = PlaybackErrorMapper.fromPlayerError({
        category: "DRM",
        code: errorCode,
        message: "",
        severity: PlayerErrorSeverity.RECOVERABLE
      });
      VideoProviderConfigurationLogger.logPlaybackFailed(
        errorCode,
        this.getCurrentPlayingproviderId() || "",
        errorCount
      );
      this.mPlayerError = error;
      this.mPlaybackErrorSubscription.notify(error);
    }
  }

  private getUpdatedDomainConfig(videoProviderState: VideoProviderState): void {
    this.log("info", "getUpdatedDomainConfig() - " + videoProviderState);
    switch (videoProviderState) {
      case VideoProviderState.SUCCESS:
        if (this.mDrmRequestGeneration !== this.mTuneGeneration) {
          this.log(
            "info",
            "getUpdatedDomainConfig() - no DRM setup awaiting SUCCESS, ignoring"
          );
          break;
        }
        this.mDrmRequestGeneration = null;
        const drmConfig = this.mDrmManager.getDRMConfig();
        this.mVideoManager.setupDRM(drmConfig);
        this.onDrmConfigured();
        break;
      case VideoProviderState.REFRESH:
        this.onDrmRefresh();
        break;
      case VideoProviderState.ERROR:
        this.onDrmFail();
        break;
      default:
        break;
    }
  }

  /**
   * Handles HTTP 401/409 DRM enforcement errors surfaced via the DRM_FAILED
   * player event. Force-bypasses the SDT cache to get a fresh scope-domain
   * token, then restarts playback. Bounded by mDrmEnforcementRetryCount so
   * repeated enforcement failures eventually surface as DRM_ERROR.
   */
  private async onDrmEnforcementFail(): Promise<void> {
    this.log("error", "onDrmEnforcementFail()");
    this.mDrmEnforcementRetryCount += 1;
    if (
      this.mDrmEnforcementRetryCount <= MAX_DRM_ERROR_RETRY_COUNT &&
      this.mPlayable !== null
    ) {
      const providerId = this.getCurrentPlayingproviderId() || "";
      // Force-bypass the SDT cache so a fresh scope-domain token is fetched
      // from the API. The fresh token is picked up by the next license request.
      await this.setupDrm(providerId, true);
    } else {
      this.log("error", "onDrmEnforcementFail() - max retries exceeded");
      const error: PlaybackError = PlaybackErrorMapper.fromPlayerError({
        category: "DRM",
        code: playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR,
        message: "",
        severity: PlayerErrorSeverity.RECOVERABLE
      });
      this.mPlaybackErrorSubscription.notify(error);
    }
  }

  private onDrmRefresh(): void {
    this.log("info", "onDrmRefresh()");
    const drmConfig = this.mDrmManager.getDRMConfig();
    if (drmConfig !== null && drmConfig !== undefined) {
      this.mVideoManager.updateDRMInfo(drmConfig);
    }
  }

  // ── VideoManager / Player State Listener ─────────────────────────────────

  private onPlayerStateChangeHandler(state: PlayerState): void {
    const previousState = this.mVideoPlayerCurrentState;
    this.mVideoPlayerCurrentState = state;
    this.log("debug", "onPlayerStateChangeHandler() - " + state);

    switch (state) {
      case PlayerState.LOADING:
        this.log("debug", "onPlayerStateChangeHandler() - LOADING");
        break;
      case PlayerState.READY:
        this.onPlayerStateLoaded();
        break;
      case PlayerState.PLAYING:
        this.onPlayerStatePlaying();
        break;
      case PlayerState.ENDED:
        this.onEndOfStream();
        break;
      case PlayerState.PAUSED:
        this.onPlayerStatePause();
        break;
      case PlayerState.IDLE:
        this.onPlayerStateUnLoaded();
        break;
      case PlayerState.BUFFERING:
        this.log("debug", "onPlayerStateChangeHandler() - BUFFERING");
        break;
      case PlayerState.SEEKING:
        this.log("debug", "onPlayerStateChangeHandler() - SEEKING");
        break;
      case PlayerState.ERROR:
        this.log("error", "onPlayerStateChangeHandler() - ERROR");
        this.clearLiveResumeTimer();
        break;
      case PlayerState.FIRST_FRAME:
        this.onFirstFrame();
        break;
      case PlayerState.INITIATION:
        this.onPlayerInitiation();
        break;
      case PlayerState.DRM_CONFIGURED:
        this.log("debug", "onPlayerStateChangeHandler() - DRM_CONFIGURED");
        break;
      case PlayerState.DRM_FAILED:
        this.onDrmEnforcementFail();
        break;
      default:
        break;
    }

    const tpm = this.mVideoManager.getTrickPlayManager();
    const isTrickPlayActive = tpm !== null && tpm.isActive();
    this.mPlaybackSessionCoordinator.onPlayerStateChanged(
      previousState,
      state,
      isTrickPlayActive
    );

    this.mPlayerStateSubscription.notify(state);
  }

  /**
   * Pauses a stream that reached the media element while the player is Hidden.
   *
   * isLoadWithheldWhileHidden() refuses loads requested while Hidden, but a
   * load already in flight when the player went Hidden still completes and
   * autoplays: the pause() inside hidden() runs before the media element has
   * anything to pause, and the SDK plays on load. This is the backstop for that
   * race — anything that reaches the element with no player surface is paused
   * again, keeping the resume intent for restoreFromHidden().
   */
  private pauseIfHidden(caller: string): void {
    if (this.mPlayerViewMode !== PlayerViewMode.Hidden) {
      return;
    }
    Log.i(VideoPlaybackController.TAG, caller + " - paused, player is Hidden");
    this.mWasPlayingBeforeHidden = true;
    this.pause();
  }

  private onPlayerStateLoaded(): void {
    this.log("debug", "onPlayerStateLoaded()");
    this.mIsPlayerLoaded = true;
    this.handleAudioPreference();
    this.handleTextPreference();
    // TODO: Check for unsupported content
    this.pauseIfHidden("onPlayerStateLoaded()");
  }

  private onPlayerStatePlaying(): void {
    this.log("debug", "onPlayerStatePlaying()");
    this.releaseAppliedNetworkRestoreSeek();
    this.mDrmEnforcementRetryCount = 0;
    this.mIsPlaying = true;
    this.mIsTuneInProgress = false;
    this.clearLiveResumeTimer();
    if (!this.isLive()) {
      this.setLastViewedPositionInterval();
    } else {
      const channelNumber: string | undefined =
        this.mPlayable !== null && this.mPlayable.channel !== undefined
          ? this.mPlayable.channel.channelNumber
          : undefined;
      if (channelNumber) {
        DeviceModel.setLastTunedChannelNumber(channelNumber).catch(() => {});
      }
      if (this.mPlayerViewMode === PlayerViewMode.FullScreen) {
        // Start watch history timer for live content
        this.startLiveWatchThresholdTimer();
      }
    }
    this.pauseIfHidden("onPlayerStatePlaying()");
  }

  //  Live Watch-History

  private startLiveWatchThresholdTimer(): void {
    this.clearLiveWatchThresholdTimer();
    if (!this.isLive()) {
      return;
    }
    const channel =
      this.mPlayable !== null ? this.mPlayable.channel : undefined;
    const channelId = channel !== undefined ? channel.channelId : undefined;
    const stationId = channel !== undefined ? channel.stationId : undefined;
    if (!channelId || !stationId) {
      this.log(
        "warn",
        "startLiveWatchThresholdTimer() — channelId/stationId missing, not starting timer"
      );
      return;
    }
    this.mLiveWatchThresholdTimerId = setTimeout(() => {
      this.mLiveWatchThresholdTimerId = null;
      // 5 min uninterrupted → "watched". Snapshot THIS content now so a later
      // channel/screen switch during the jitter still posts the previous item.
      this.scheduleLiveWatchHistoryJitter({ channelId, stationId });
    }, LIVE_WATCH_HISTORY_DELAY_MS);
  }

  private clearLiveWatchThresholdTimer(): void {
    if (this.mLiveWatchThresholdTimerId !== null) {
      clearTimeout(this.mLiveWatchThresholdTimerId);
      this.mLiveWatchThresholdTimerId = null;
    }
  }

  private scheduleLiveWatchHistoryJitter(item: TLiveWatchHistoryItem): void {
    const jitterMs = Math.floor(
      // eslint-disable-next-line sonarjs/pseudo-random
      Math.random() * LIVE_WATCH_HISTORY_JITTER_MAX_MS
    );
    this.log("debug", "scheduleLiveWatchHistoryJitter() - " + jitterMs);
    const id = setTimeout(() => {
      this.mLiveWatchJitterTimerIds.delete(id);
      this.postLiveWatchHistory(item);
    }, jitterMs);
    this.mLiveWatchJitterTimerIds.add(id);
  }

  private clearAllLiveWatchJitterTimers(): void {
    for (const id of this.mLiveWatchJitterTimerIds) {
      clearTimeout(id);
    }
    this.mLiveWatchJitterTimerIds.clear();
  }

  private postLiveWatchHistory(item: TLiveWatchHistoryItem): void {
    try {
      new WatchHistoryInteractor()
        .saveLiveRecentlyWatched(item)
        .then(() => {
          this.log("info", "live watch history recorded - " + item.channelId);
        })
        .catch((error: unknown) => {
          this.log("error", "postLiveWatchHistory()", error);
        });
    } catch (error: unknown) {
      this.log("error", "postLiveWatchHistory()", error);
    }
  }

  private onPlayerStatePause(): void {
    this.clearLastViewedPositionInterval();
    this.scheduleLiveResumeTimer();
    // Paused position doesn't drift, so save it now — this is the resume
    // point regardless of how/when the user later exits or stops.
    if (!this.isLive()) {
      this.updateLastViewedPosition();
    }
  }

  private onPlayerStateUnLoaded(): void {
    this.mIsPlaying = false;
    this.mIsPlayerLoaded = false;
    this.clearLastViewedPositionInterval();
    this.clearLiveResumeTimer();
  }

  private onEndOfStream(): void {
    this.log("info", "onEndOfStream()");
    this.mPlaybackSessionCoordinator.onPlaybackStopped(
      SessionEndReason.END_OF_CONTENT
    );
    if (this.isNdvr()) {
      this.exitEndedProgram();
    } else if (this.isStartOver()) {
      // Go through switchToLiveTv() while the playable is still Start Over, as
      // Go LIVE does. Relabelling it LIVE first (switchPlaybackContentType)
      // makes startLivePlayback() treat the ended SOCU stream as the live one
      // and only resume() it, leaving the last frame on screen.
      if (this.getMediaPaneType() === MediaPaneType.TMIS) {
        this.switchToLiveTv();
      }
    } else if (this.isSocu()) {
      this.switchToLiveTv();
    } else if (this.isVod()) {
      // A preview ends where the screen that started it takes over, so the
      // player only lets go of it.
      if (this.isVodPreview()) {
        this.stop();
      } else {
        this.exitEndedProgram();
      }
    } else {
      this.switchToLiveTv();
    }
  }

  /**
   * Leaves a VOD title or nDVR recording that has played to its end.
   *
   * Full screen → returns to the screen that opened the player (navigation is
   * performed by the view layer). Picture-in-Picture or any other view →
   * switches to Live TV, which also records the final position.
   */
  private exitEndedProgram(): void {
    this.updateLastViewedPosition();
    const isFullScreenMode: boolean =
      this.mPlayerViewMode === PlayerViewMode.FullScreen ||
      this.mPlayerViewMode === PlayerViewMode.BrowserFullScreen;
    if (!isFullScreenMode) {
      this.log("info", "exitEndedProgram() - switching to Live TV");
      this.switchToLiveTv();
      return;
    }
    this.log("info", "exitEndedProgram() - returning to previous screen");
    // Saved before stop(), which unloads the player and loses the position.
    this.updateLastViewedPosition();
    this.stop();
    this.mExitToPreviousScreenSubscription.notify();
  }

  private onFirstFrame(): void {
    this.log("debug", "onFirstFrame()");
    this.mHasPreviousContentPlayed = true;
    if (this.mPlayable !== null) {
      if (
        this.mPlayable.playbackContentType === PlaybackContentType.LIVE &&
        // Skipped for TiVo+: the override narrows the seekable window for
        // timeshift, and TiVo+ has none — trick-play is disabled outright, so
        // there is nothing for it to constrain. It is also the one manifest
        // shape where the call cannot succeed, because reconfiguring a loaded
        // muxed-HLS stream throws inside the player's variant filter.
        !this.isTivoPlusPlayable()
      ) {
        this.mVideoManager.overrideAvailabilityWindowForLinear();
      } else if (this.isStartOver()) {
        this.mVideoManager.overrideAvailabilityWindowForStartOver();
      }
    }
    // A reload that follows reconnection returns to where the viewer was cut
    // off. It outranks the Start Over seeks below: those place a *new* Start
    // Over at its entry point, whereas this is the same one resuming.
    const networkRestoreOffset = this.takeNetworkRestoreOffset();
    if (networkRestoreOffset !== null) {
      const range = this.getSeekRange();
      const target = range.start + networkRestoreOffset;
      const clampedTarget =
        range.end > range.start ? Math.min(target, range.end) : target;
      this.log(
        "info",
        "onFirstFrame() - restoring position after reconnect: " +
          String(clampedTarget)
      );
      this.mVideoManager.setPlayPosition(clampedTarget);
      this.mStartOverResumeSeconds = null;
    } else if (
      this.mPlayable !== null &&
      this.getMediaPaneType() === MediaPaneType.TMIS
    ) {
      const range = this.getSeekRange();
      if (
        this.mPlayable.playbackContentType ===
        PlaybackContentType.STARTOVER_BEGINNING
      ) {
        this.mVideoManager.setPlayPosition(range.start);
      } else if (
        this.mStartOverResumeSeconds !== null &&
        this.mPlayable.playbackContentType ===
          PlaybackContentType.STARTOVER_RESUME
      ) {
        const target = range.start + (this.mStartOverResumeSeconds || 0);
        this.mVideoManager.setPlayPosition(target);
      }
      this.mStartOverResumeSeconds = null;
    }
    this.mSeekRangeStartTime = this.getSeekRange().start;
    // The seekable range is first known here, after the availability
    // override above has settled the live window.
    this.refreshSeekableWindowEligibility();
  }

  private onPlayerInitiation(): void {
    this.log("debug", "onPlayerInitiation()");
    this.mPlayerError = null;
  }

  private onPlaybackErrorHandler(error: TPlayerError): void {
    this.log("error", "onPlaybackErrorHandler()", error);
    if (error.severity === PlayerErrorSeverity.CRITICAL) {
      this.mPlaybackSessionCoordinator.onPlaybackStopped(
        SessionEndReason.PLAYER_ERROR,
        error
      );
      // The player unloads its source on a critical error, which stops the
      // segment requests but does not pause the <video> element itself, so
      // without this the element can keep trying to play against a dead
      // session. Unforced — a system pause, not a user one — matching
      // onNetworkDisconnected()/onAppBackground().
      if (!this.mIsForcedPaused) {
        this.pause();
      }
    } else {
      this.mPlaybackSessionCoordinator.recordPlaybackError(error);
    }
    const playbackError = PlaybackErrorMapper.fromPlayerError(error);
    this.mPlayerError = playbackError;
    this.mPlaybackErrorSubscription.notify(playbackError);
  }

  private onPlayerViewHandler(mode: PlayerViewMode): void {
    this.log("info", "onPlayerViewHandler() - " + mode);
    this.mPlayerViewMode = mode;
    this.updateClosedCaptionVisibilityForViewMode(mode);
    this.mPlayerViewModeSubscription.notify(mode);
  }

  private updateClosedCaptionVisibilityForViewMode(mode: PlayerViewMode): void {
    const isFullScreenMode: boolean =
      mode === PlayerViewMode.FullScreen ||
      mode === PlayerViewMode.BrowserFullScreen;

    if (isFullScreenMode) {
      if (this.mIsClosedCaptionHiddenForViewMode) {
        this.mIsClosedCaptionHiddenForViewMode = false;
        this.mVideoManager.setTextTrackVisibility(true);
      }
      return;
    }

    if (this.isClosedCaptionActive()) {
      this.mIsClosedCaptionHiddenForViewMode = true;
      this.mVideoManager.setTextTrackVisibility(false);
    }
  }

  /**
   * Whether a media load must be withheld because the player has no surface.
   *
   * Bringing playback up is asynchronous (metadata -> DRM -> session), so the
   * player can be switched to PlayerViewMode.Hidden while a tune is still in
   * flight: hidden() pauses a source that has not loaded yet, and the load then
   * lands with autoplay and starts audio and video behind a screen that has no
   * player surface. Withholding the load records the resume intent instead --
   * restoreFromHidden() re-acquires the source as soon as a screen claims a
   * visible view mode again.
   */
  private isLoadWithheldWhileHidden(caller: string): boolean {
    if (this.mPlayerViewMode !== PlayerViewMode.Hidden) {
      return false;
    }
    Log.i(
      VideoPlaybackController.TAG,
      caller + " - withheld, player is Hidden"
    );
    this.mWasPlayingBeforeHidden = true;
    return true;
  }

  private initPlayback(startPosition?: number): void {
    Log.i(VideoPlaybackController.TAG, "initPlayback()");
    if (this.isLoadWithheldWhileHidden("initPlayback()")) {
      return;
    }
    if (this.mPlayable === null) {
      this.log("error", "initPlayback() - playable is null");
      return;
    }
    const drmConfig = this.mDrmManager.getDRMConfig();
    const url = this.mPlaybackURLManager.getPlaybackUrl(
      this.mPlayable,
      this.mMetadataManager.getMetadata(),
      drmConfig,
      undefined
    );
    if (url === "") {
      this.log("error", "initPlayback() - no matching URI found");
      // LIVE has no second source of a playback URL: the tuning URI is all
      // there is, so an empty one is a dead end and must surface as an error
      // rather than leaving the player mounted on nothing. SOCU is deliberately
      // excluded — a channel delivering no usable socu:// entry legitimately
      // has no pre-session URL and is expected to play once the session
      // responds, which is what it did before that path existed.

      //TODO : Need to look into this error
      if (this.isLive()) {
        /* eslint-disable sonarjs/no-commented-code -- kept verbatim as the
           reference implementation to restore once the TODO above is resolved */
        // this.mPlaybackErrorSubscription.notify(
        //   new PlaybackError(
        //     PlaybackSetupErrorCode.PLAYLIST_URI_MISSING,
        //     Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE)
        //   )
        // );
        /* eslint-enable sonarjs/no-commented-code */
      }
      return;
    }
    this.mVideoManager.playUri(url, startPosition);
  }

  // ── Playback Control Delegation ──────────────────────────────────────────

  public pause(forced: boolean = false): void {
    this.mIsPlaying = false;
    this.mVideoManager.pause();
    this.mIsForcedPaused = forced || this.mIsForcedPaused;
  }

  public isPictureInPictureSupported(): boolean {
    return (
      document.pictureInPictureEnabled === true &&
      "requestPictureInPicture" in HTMLVideoElement.prototype
    );
  }

  public getPlaybackVideoElement(): HTMLVideoElement | null {
    const candidate: Element | null = document.querySelector(
      ".video-player-container video"
    );
    if (candidate !== null && candidate instanceof HTMLVideoElement) {
      return candidate;
    }
    return null;
  }

  public hasActiveFullscreenElement(): boolean {
    const doc: Document & {
      webkitFullscreenElement?: Element | null;
      mozFullScreenElement?: Element | null;
      msFullscreenElement?: Element | null;
    } = document;

    return (
      doc.fullscreenElement != null ||
      doc.webkitFullscreenElement != null ||
      doc.mozFullScreenElement != null ||
      doc.msFullscreenElement != null
    );
  }

  public shouldReturnToPlayer(returnIntended: boolean): boolean {
    if (!returnIntended) {
      return false;
    }
    if (this.hasActiveFullscreenElement()) {
      return true;
    }
    return document.visibilityState === "visible" && document.hasFocus();
  }

  public registerBeforeNavigateHandler(handler: BeforeNavigateHandler): void {
    this.mBeforeNavigateHandler = handler;
  }

  public unregisterBeforeNavigateHandler(handler: BeforeNavigateHandler): void {
    if (this.mBeforeNavigateHandler === handler) {
      this.mBeforeNavigateHandler = null;
    }
  }

  public notifyBeforeNavigate(targetUrl: string | null): void {
    if (this.mBeforeNavigateHandler !== null) {
      this.mBeforeNavigateHandler(targetUrl);
    }
  }

  public resume(): void {
    // A hidden player has no surface, so it must never produce audio or video.
    // Record the intent instead — restoreFromHidden() replays it as soon as a
    // screen claims a visible view mode. This single guard covers every resume
    // trigger (app foreground, network reconnect, restriction lifted, PiP).
    if (this.mPlayerViewMode === PlayerViewMode.Hidden) {
      this.log("debug", "resume() - deferred, player is Hidden");
      this.mWasPlayingBeforeHidden = true;
      this.mIsForcedPaused = false;
      return;
    }
    this.mIsPlaying = true;
    this.mVideoManager.resume();
    this.mIsForcedPaused = false;
  }

  public playUri(uri: string, startPosition?: number): void {
    if (this.isLoadWithheldWhileHidden("playUri()")) {
      return;
    }
    if (!this.isUnsupportedContent()) {
      if (startPosition !== undefined && startPosition !== null) {
        this.mVideoManager.playUri(uri, startPosition);
      } else {
        this.mVideoManager.playUri(uri);
      }
    }
  }

  public setPlayPosition(position: number): void {
    this.mVideoManager.setPlayPosition(position);
  }

  // ── View Mode Delegation ─────────────────────────────────────────────────

  /**
   * Applies a view-mode change and, when leaving PlayerViewMode.Hidden, replays
   * the deferred resume intent.
   *
   * The mode is applied first because VideoManager notifies synchronously, so
   * `mPlayerViewMode` is already the new (visible) mode by the time
   * restoreFromHidden() runs — which is what lets it call resume() without
   * tripping the Hidden guard in resume().
   */
  private switchViewMode(applyViewMode: () => void): void {
    const wasHidden: boolean = this.mPlayerViewMode === PlayerViewMode.Hidden;
    applyViewMode();
    if (wasHidden) {
      this.restoreFromHidden();
    }
  }

  /**
   * Restores playback after the player leaves Hidden mode.
   *
   * Resumes in place when the media source survived, or re-acquires it when the
   * stream was torn down while hidden. A user-forced pause is honoured — the
   * player becomes visible but stays paused.
   */
  private restoreFromHidden(): void {
    const shouldResume: boolean = this.mWasPlayingBeforeHidden;
    this.mWasPlayingBeforeHidden = false;

    this.log(
      "info",
      "restoreFromHidden() - shouldResume=" +
        String(shouldResume) +
        VideoPlaybackController.LOG_IS_PLAYER_LOADED +
        String(this.mIsPlayerLoaded) +
        VideoPlaybackController.LOG_IS_FORCED_PAUSED +
        String(this.mIsForcedPaused)
    );

    // A user-forced pause outranks everything: the player becomes visible again
    // but stays paused where they left it.
    if (this.mIsForcedPaused) {
      return;
    }

    if (!this.mIsPlayerLoaded) {
      // The stream was torn down while hidden (long background, or the Hidden
      // branch of switchToLiveTv). The destination screen has a player surface,
      // and a visible surface with no source is always wrong — so re-acquire
      // regardless of the resume intent.
      if (this.mPlayable !== null) {
        this.restorePlayback();
      }
      return;
    }

    if (shouldResume) {
      this.resume();
    }
  }

  /**
   * Enters Hidden view mode: pauses playback and collapses the player out of
   * view without unloading the media source, session or playhead.
   *
   * Intended for screens that have no player surface (My Hub, Settings, ...).
   * Returning to a screen that claims a visible view mode resumes playback from
   * the same position via restoreFromHidden().
   */
  public hidden(): void {
    // Native PiP puts the <video> in an OS-level window the user is still
    // watching — the in-page container's visibility is irrelevant and pausing
    // would stop the PiP window.
    if (this.isBrowserPictureInPictureTransitionActive()) {
      this.log("debug", "hidden() - skipped, browser PiP active");
      return;
    }

    if (this.mPlayerViewMode === PlayerViewMode.Hidden) {
      return;
    }

    // Capture before pause() clears mIsPlaying. mIsPlaying covers the window
    // between startPlayback() and the first PLAYING state change, where no
    // media event has landed yet.
    this.mWasPlayingBeforeHidden =
      this.mIsPlaying ||
      this.mVideoPlayerCurrentState === PlayerState.PLAYING ||
      this.mVideoPlayerCurrentState === PlayerState.BUFFERING ||
      this.mVideoPlayerCurrentState === PlayerState.SEEKING;

    this.log(
      "info",
      "hidden() - wasPlaying=" + String(this.mWasPlayingBeforeHidden)
    );

    this.pause();
    this.mVideoManager.hidden();
  }

  /** Whether the player is currently in Hidden view mode. */
  public isPlaybackHidden(): boolean {
    return this.mPlayerViewMode === PlayerViewMode.Hidden;
  }

  public fullScreen(): void {
    this.switchViewMode((): void => this.mVideoManager.fullScreen());
  }

  public pip(): void {
    this.switchViewMode((): void => this.mVideoManager.pip());
  }

  public getPictureInPictureLeaveResolveTimeoutMs(): number {
    return 1000;
  }

  public enterBrowserPictureInPictureMode(): void {
    this.mBrowserPiPActive = true;
    this.mPipEntryPending = false;
    this.pip();

    const playing: boolean =
      this.mVideoPlayerCurrentState === PlayerState.PLAYING ||
      this.mVideoPlayerCurrentState === PlayerState.BUFFERING;
    this.mPlayingOnPiPEntry = playing;

    if (playing) {
      globalThis.setTimeout((): void => {
        if (this.isBrowserPictureInPictureTransitionActive()) {
          this.resume();
        }
      }, 0);
    }
  }

  public exitBrowserPictureInPictureMode(): void {
    this.mBrowserPiPActive = false;
    this.mPiPReturnPending = true;
    this.fullScreen();
  }

  public closeBrowserPictureInPictureMode(): Promise<void> {
    this.mBrowserPiPActive = false;
    this.mPiPReturnPending = false;
    this.mPlayingOnPiPEntry = false;
    this.mPipEntryPending = false;
    this.mReturnToPlayerIntended = false;
    this.mPipToGuideConversionActive = false;
    return this.stop();
  }

  public clearBrowserPictureInPictureMode(): void {
    this.mBrowserPiPActive = false;
    this.mPiPReturnPending = false;
    this.mPlayingOnPiPEntry = false;
    this.mPipEntryPending = false;
    this.mReturnToPlayerIntended = false;
    this.mPipToGuideConversionActive = false;
  }

  public isBrowserPictureInPictureTransitionActive(): boolean {
    return (
      this.mBrowserPiPActive ||
      this.mPipEntryPending ||
      // Nullish check, not `!== null`: browsers without the PiP API leave
      // pictureInPictureElement undefined, which must not read as "PiP active".
      document.pictureInPictureElement != null
    );
  }

  public isBrowserPiPActive(): boolean {
    return this.mBrowserPiPActive;
  }

  public setBrowserPiPActive(value: boolean): void {
    this.mBrowserPiPActive = value;
  }

  public wasPlayingOnPiPEntry(): boolean {
    return this.mPlayingOnPiPEntry;
  }

  public setPlayingOnPiPEntry(value: boolean): void {
    this.mPlayingOnPiPEntry = value;
  }

  public isPiPReturnPending(): boolean {
    return this.mPiPReturnPending;
  }

  public setPiPReturnPending(value: boolean): void {
    this.mPiPReturnPending = value;
  }

  public isPipEntryPending(): boolean {
    return this.mPipEntryPending;
  }

  public setPipEntryPending(value: boolean): void {
    this.mPipEntryPending = value;
  }

  public isReturnToPlayerIntended(): boolean {
    return this.mReturnToPlayerIntended;
  }

  public setReturnToPlayerIntended(value: boolean): void {
    this.mReturnToPlayerIntended = value;
  }

  public isPipToGuideConversionActive(): boolean {
    return this.mPipToGuideConversionActive;
  }

  public setPipToGuideConversionActive(value: boolean): void {
    this.mPipToGuideConversionActive = value;
  }

  public isNavigatingToPlayer(): boolean {
    return this.mIsNavigatingToPlayer;
  }

  public setNavigatingToPlayer(value: boolean): void {
    this.mIsNavigatingToPlayer = value;
  }

  public pig(config: TPigConfig): void {
    this.switchViewMode((): void => this.mVideoManager.pig(config));
  }

  public atmospheric(): void {
    // Atmospheric playback is supported on TV devices as background video.
    // Desktop does not render the atmospheric surface, so it transitions to
    // Hidden after applying the view-mode update.
    // The user has left the immersive watch view — the current watch session
    // is over for analytics purposes even though the stream itself keeps
    // running as atmospheric background video. No-op if already closed.
    this.mPlaybackSessionCoordinator.onPlaybackStopped(
      SessionEndReason.USER_ENDED
    );
    const isTvDevice: boolean = DeviceModel.isTvDevice();
    if (!isTvDevice) {
      // Desktop atmospheric tears the stream down, so any deferred Hidden
      // resume intent is moot — drop it rather than resume-then-stop.
      this.mWasPlayingBeforeHidden = false;
    }
    this.switchViewMode((): void => this.mVideoManager.atmospheric());
    if (!isTvDevice) {
      this.hidden();
    }
  }

  public browserFullScreen(): void {
    this.switchViewMode((): void => this.mVideoManager.browserFullScreen());
  }

  public getMediaPaneType(): MediaPaneType {
    // TODO: remove the hardcoded value
    return MediaPaneType.TMIS;
  }

  // ── Trick-Play Delegation ────────────────────────────────────────────────

  /**
   * Initialize trick-play for the current playback session.
   * Call after playback is loaded and capability information is available.
   *
   * @param config - Trick-play configuration values.
   * @param eligibility - Platform x channel eligibility rules.
   */
  public initTrickPlay(
    config: ITrickPlayConfig,
    eligibility: ITrickPlayEligibility
  ): void {
    this.mVideoManager.initTrickPlay(config, eligibility);
  }

  /**
   * Get the TrickPlayManager instance for direct trick-play control.
   *
   * @returns The TrickPlayManager, or null if not initialized.
   */
  public getTrickPlayManager(): TrickPlayManager | null {
    return this.mVideoManager.getTrickPlayManager();
  }

  /**
   * Handle discrete skip intent (SKIP_FORWARD / SKIP_BACKWARD).
   *
   * @param direction - Forward (+1) or Backward (-1).
   */
  public trickPlaySkip(direction: Direction): boolean {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      return tpm.skip(direction);
    }
    return false;
  }

  /**
   * Handle hold intent (HOLD_FORWARD / HOLD_BACKWARD).
   *
   * @param direction - Forward (+1) or Backward (-1).
   * @param level - Speed escalation level.
   */
  public trickPlayHold(direction: Direction, level: SpeedLevel): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.hold(direction, level);
    }
  }

  /**
   * Handle speed-cycle intent (FF / RW button press).
   *
   * @param direction - Forward (+1) or Backward (-1).
   */
  public trickPlaySpeedCycle(direction: Direction): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.speedCycle(direction);
    }
  }

  /**
   * Handle scrub begin intent.
   *
   * @param positionSec - Starting scrub position in seconds.
   */
  public trickPlayScrubBegin(positionSec: number): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.scrubBegin(positionSec);
    }
  }

  /**
   * Handle scrub move intent.
   *
   * @param positionSec - Current scrub target position in seconds.
   */
  public trickPlayScrubMove(positionSec: number): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.scrubMove(positionSec);
    }
  }

  /**
   * Handle scrub end intent.
   *
   * @param positionSec - Final committed position in seconds.
   */
  public trickPlayScrubEnd(positionSec: number): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.scrubEnd(positionSec);
    }
  }

  /**
   * Confirm the current trick-play session and resume playback.
   */
  public trickPlayConfirm(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.confirm();
    }
  }

  /**
   * Cancel the current trick-play session and return to entry point.
   */
  public trickPlayCancel(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.cancel();
    }
  }

  /**
   * Stop the current trick-play session without seeking back.
   * Used for hold-release (stay at position, remain paused).
   */
  public trickPlayStop(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.stop();
    }
  }

  /**
   * Pause the active seek motion but keep the session alive.
   * Called on hold key-release so the user can OK-confirm or BACK-cancel.
   */
  public trickPlayHoldRelease(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.holdRelease();
    }
  }

  /**
   * Update the restriction API state for trick-play (TP-AP-1..3).
   * Called by the restriction API handler after the API resolves or recovers.
   *
   * @param enabled - True if trick-play is allowed, false if restricted.
   */
  public setTrickPlayRestrictionEnabled(enabled: boolean): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.setRestrictionEnabled(enabled);
    }
  }

  /**
   * Set whether a dedicated Start Over (SOCU) stream is available for the
   * current airing. Derived from `isAvailableInSocu` in the v4/preview response.
   * Called by the ViewModel when airing preview data loads.
   */
  public setStartOverAvailable(available: boolean): void {
    this.mStartOverAvailable = available;
    const session = this.mSessionManager.getSession();
    if (session !== null) {
      this.applyTrickPlayRestrictionsFromSession(session);
    }
  }

  /**
   * Whether a dedicated Start Over (SOCU) stream is available.
   */
  public getStartOverAvailable(): boolean {
    return this.mStartOverAvailable;
  }

  /**
   * Update trick-play eligibility rules (e.g. on channel change, PRD §5).
   *
   * @param eligibility - New eligibility state.
   */
  public setTrickPlayEligibility(eligibility: ITrickPlayEligibility): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.setEligibility(eligibility);
    }
  }

  /**
   * Whether trick-play is currently enabled (restriction API + eligibility).
   * UI uses this to show/disable the seek bar (TP-AP-4).
   *
   * @returns True if trick-play controls should be interactive.
   */
  public isTrickPlayEnabled(): boolean {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null && tpm.isEnabled();
  }

  public isThumbnailModeEnabled(): boolean {
    // TODO: enableThumbnailSeek flag beed to be used to check if thumbnail mode is enabled or not. For now, returning true.
    return this.getMediaPaneType() === MediaPaneType.TMIS;
  }

  /**
   * Get the active trick-play eligibility (PRD §5).
   */
  public getTrickPlayEligibility(): ITrickPlayEligibility | null {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getEligibility() : null;
  }

  /**
   * Whether the restriction API currently allows trick-play (TP-AP-1).
   */
  public isTrickPlayRestrictionEnabled(): boolean {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null && tpm.isRestrictionEnabled();
  }

  /**
   * Whether a trick-play session is currently active.
   * UI uses this to swap Pause icon to Play (TP-SP-11).
   *
   * @returns True if a seek session is in progress.
   */
  public isTrickPlayActive(): boolean {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null && tpm.isActive();
  }

  /**
   * Get the currently active trick-play mode.
   * UI uses this to select preview type: thumbnail overlay,
   * fullscreen i-frame, or no preview (TP-SP-3).
   *
   * @returns The active mode, or null if not initialized.
   */
  public getTrickPlayMode(): TrickPlayMode | null {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getMode() : null;
  }

  /**
   * Get the seek entry point for the current trick-play session.
   * UI uses this to render the entry point marker on the progress bar (TP-LP-4).
   *
   * @returns Entry point in seconds, or null if no session is active.
   */
  public getTrickPlaySeekEntryPoint(): number | null {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getSeekEntryPoint() : null;
  }

  /**
   * Get the impl-tracked target position for the current trick-play session.
   * For Thumbnail mode this is the virtual seek target (player stays at
   * entry point during hold); for Emulated/IFrame it returns the player's
   * current time. Falls back to player current time when no manager exists.
   *
   * @returns Target position in seconds.
   */
  public getTrickPlayTargetPosition(): number {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getTargetPosition() : this.getPlayerCurrentTime();
  }

  /**
   * Get the current speed level during an active hold/cycle session.
   * UI uses this to render the speed indicator (TP-LP-3, TP-FF-3).
   *
   * @returns The active speed level.
   */
  public getTrickPlayCurrentLevel(): SpeedLevel | null {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getCurrentLevel() : null;
  }

  /**
   * Get the current seek direction during an active hold/cycle session.
   * UI uses this to render the speed indicator direction.
   *
   * @returns The active direction, or null if idle.
   */
  public getTrickPlayCurrentDirection(): Direction | null {
    const tpm = this.mVideoManager.getTrickPlayManager();
    return tpm !== null ? tpm.getCurrentDirection() : null;
  }

  /**
   * Notify that the player reached a seekable range boundary during
   * active trick-play. Called by the TIME_UPDATE boundary watcher.
   * Implements TP-LP-9, TP-FF-8 boundary auto-stop.
   *
   * @param direction - The direction that hit the boundary.
   */
  public onTrickPlayBoundaryReached(direction: Direction): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.onBoundaryReached(direction);
    }
  }

  /**
   * Jump to the live edge (seek range end).
   * Stops any active trick-play and seeks to live.
   * Only allowed when goLiveAvailable eligibility is set (PRD §5).
   */
  public trickPlayGoToLive(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.goToLive();
    }
  }

  /**
   * Jump to the start of the seekable buffer.
   * Stops any active trick-play and seeks to buffer start.
   * Only allowed when startOverAvailable eligibility is set (PRD §5).
   */
  public trickPlayStartFromBuffer(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null) {
      tpm.startFromBuffer();
    }
  }

  // ── Content Type Checkers ────────────────────────────────────────────────

  public isLive(): boolean {
    return (
      this.mPlayable !== null &&
      this.mPlayable.playbackContentType === PlaybackContentType.LIVE
    );
  }

  public isNdvr(): boolean {
    return (
      this.mPlayable !== null &&
      (this.mPlayable.playbackContentType === PlaybackContentType.NDVR_LIVE ||
        this.mPlayable.playbackContentType ===
          PlaybackContentType.NDVR_RECORDED)
    );
  }

  public isVod(): boolean {
    return (
      this.mPlayable !== null &&
      this.mPlayable.playbackContentType === PlaybackContentType.VOD
    );
  }

  public isSocu(): boolean {
    return this.isStartOver() || this.isCatchUp();
  }

  public isStartOver(): boolean {
    return (
      this.mPlayable !== null &&
      (this.mPlayable.playbackContentType ===
        PlaybackContentType.STARTOVER_BEGINNING ||
        this.mPlayable.playbackContentType ===
          PlaybackContentType.STARTOVER_RESUME)
    );
  }

  public isCatchUp(): boolean {
    return (
      this.mPlayable !== null &&
      (this.mPlayable.playbackContentType ===
        PlaybackContentType.CATCHUP_BEGINNING ||
        this.mPlayable.playbackContentType ===
          PlaybackContentType.CATCHUP_RESUME)
    );
  }

  public isNdvrLive(): boolean {
    return this.isNdvr() && this.isRecordingInProgress();
  }

  public isNdvrRecorded(): boolean {
    return this.isNdvr() && !this.isRecordingInProgress();
  }

  /**
   * Recording id of the nDVR content being played, read from its watch
   * action. Empty string when the watch action carries none.
   */
  public getNdvrRecordingId(): string {
    const watchKernel = getActionKernel(this.getMetadata().watchAction);
    return watchKernel !== undefined &&
      typeof watchKernel.recordingId === "string"
      ? watchKernel.recordingId
      : "";
  }

  public isRecordingInProgress(): boolean {
    const watchAction = this.mMetadataManager.getMetadata().watchAction;
    if (
      watchAction !== undefined &&
      watchAction !== null &&
      typeof watchAction === "object" &&
      "kernel" in watchAction
    ) {
      const kernel = watchAction.kernel as Record<string, unknown>;
      if (
        kernel !== undefined &&
        kernel !== null &&
        "isRecordingInProgress" in kernel
      ) {
        return kernel.isRecordingInProgress === true;
      }
    }
    return false;
  }

  public isLiveWithRecording(): boolean {
    if (!this.isLive()) {
      return false;
    }
    const watchAction = this.mMetadataManager.getMetadata().watchAction;
    if (
      watchAction !== undefined &&
      watchAction !== null &&
      typeof watchAction === "object" &&
      "kernel" in watchAction
    ) {
      const kernel = watchAction.kernel as Record<string, unknown>;
      if (kernel !== undefined && kernel !== null && "hasRecording" in kernel) {
        return kernel.hasRecording === true;
      }
    }
    return false;
  }

  // ── State Getters ────────────────────────────────────────────────────────

  public getPlayerState(): PlayerState | null {
    return this.mVideoPlayerCurrentState;
  }

  public isPlaying(): boolean {
    return this.mIsPlaying;
  }

  public isPlayerLoaded(): boolean {
    return this.mIsPlayerLoaded;
  }

  public isPlaybackStarted(): boolean {
    const stats = this.mVideoManager.getStats();
    return stats.playTime > 0;
  }

  public isNetworkConnected(): boolean {
    return this.mIsNetworkConnected;
  }

  public isForcePausedByUser(): boolean {
    return this.mIsForcedPaused;
  }

  public isGracePeriodFinished(): boolean {
    return this.mIsGracePeriodFinished;
  }

  public getPlayable(): IPlayable | null {
    return this.mPlayable;
  }

  public getPlaybackIntervalInfo(): IPlaybackIntervalInfo {
    return {
      currentTime: this.getPlayerCurrentTime(),
      seekRange: this.getSeekRange()
    };
  }

  public getPlayerError(): PlaybackError | null {
    return this.mPlayerError;
  }

  public getRestrictionType(): PlaybackRestrictionType {
    return this.mRestrictionType;
  }

  // ── Subscription Getters ─────────────────────────────────────────────────

  public getReadyListener(): ISubscription<boolean> {
    return this.mReadySubscription;
  }

  public getMetadataStateChangeListener(): ISubscription<MetadataState> {
    return this.mMetadataStateSubscription;
  }

  public getRestrictionListener(): ISubscription<PlaybackRestrictionType> {
    return this.mPlaybackRestrictionTypeSubscription;
  }

  public getSessionStateChangeListener(): ISubscription<SessionState> {
    return this.mSessionStateSubscription;
  }

  public getPlaybackErrorListener(): ISubscription<PlaybackError> {
    return this.mPlaybackErrorSubscription;
  }

  public getPlayerViewListener(): ISubscription<PlayerViewMode> {
    return this.mPlayerViewModeSubscription;
  }

  public getPlayerStateChangeListener(): ISubscription<PlayerState> {
    return this.mPlayerStateSubscription;
  }

  public getProgramBoundaryListener(): ISubscription<void> {
    return this.mProgramBoundarySubscription;
  }

  public getExitToPreviousScreenListener(): ISubscription<void> {
    return this.mExitToPreviousScreenSubscription;
  }

  public getPlayableChangeListener(): ISubscription<IPlayable> {
    return this.mPlayableChangeSubscription;
  }

  public getWhisperEventListener(): ISubscription<TWhisperEvent> {
    return this.mWhisperEventSubscription;
  }

  /**
   * Standalone media playback notifications: PLAYING, ENDED (end of media)
   * and ERROR. Separate from {@link getPlayerStateChangeListener}, which
   * reports main playback only.
   */
  public getStandalonePlaybackEventListener(): ISubscription<TStandalonePlaybackEvent> {
    return this.mStandalonePlaybackEventSubscription;
  }

  // ── MetadataManager Delegations ──────────────────────────────────────────

  public getMetadata(): IMetadata {
    return this.mMetadataManager.getMetadata();
  }

  /**
   * Gets the current offer ID from metadata.
   * Checks watchAction.kernel.offerId first, then falls back to airingOffers[0].offerId
   */
  public getCurrentOfferId(): string | null {
    const metadata = this.getMetadata();

    // Try to get from watchAction
    const watchKernel = getActionKernel(metadata.watchAction);
    const offerIdFromWatch: string | undefined =
      watchKernel !== undefined && typeof watchKernel.offerId === "string"
        ? watchKernel.offerId
        : undefined;
    if (offerIdFromWatch) {
      this.log(
        "debug",
        "getCurrentOfferId(): resolved from watchAction kernel"
      );
      return offerIdFromWatch;
    }

    // Fall back to airingOffers
    const airingOffers = metadata.airingOffers as unknown as
      | Array<{ offerId?: string; id?: string } | undefined>
      | undefined;
    if (airingOffers && airingOffers.length > 0) {
      const offer = airingOffers[0];
      if (offer !== undefined && offer !== null && offer.offerId) {
        this.log(
          "debug",
          "getCurrentOfferId(): resolved from airingOffers[0].offerId"
        );
        return offer.offerId;
      }
      if (offer !== undefined && offer !== null && offer.id) {
        this.log(
          "debug",
          "getCurrentOfferId(): resolved from airingOffers[0].id"
        );
        return offer.id;
      }
    }

    this.log(
      "warn",
      "getCurrentOfferId(): unable to resolve offerId from metadata"
    );
    return null;
  }

  /**
   * Gets the current content ID from playable, metadata, or session.
   */
  public getCurrentContentId(): string | null {
    const metadata = this.getMetadata();
    const watchKernel = getActionKernel(metadata.watchAction);
    const airingOffers = metadata.airingOffers as
      | Array<Record<string, unknown> | undefined>
      | undefined;
    const firstAiringOffer: Record<string, unknown> | undefined =
      airingOffers !== undefined &&
      airingOffers !== null &&
      airingOffers.length > 0
        ? airingOffers[0]
        : undefined;
    const actions = metadata.actions as
      | Array<Record<string, unknown> | undefined>
      | undefined;
    const session = this.getSession();

    const actionKernelContentIds: string[] =
      actions !== undefined && actions !== null
        ? actions.reduce(
            (
              acc: string[],
              action: Record<string, unknown> | undefined
            ): string[] => {
              const kernel = getActionKernel(action);
              const candidate =
                kernel !== undefined ? kernel.contentId : undefined;
              if (typeof candidate === "string") {
                acc.push(candidate);
              }
              return acc;
            },
            []
          )
        : [];

    const candidates: Array<unknown> = [
      this.mPlayable !== null ? this.mPlayable.contentId : undefined,
      watchKernel !== undefined ? watchKernel.contentId : undefined,
      watchKernel !== undefined ? watchKernel.partnerContentId : undefined,
      firstAiringOffer !== undefined ? firstAiringOffer.contentId : undefined,
      firstAiringOffer !== undefined
        ? firstAiringOffer.partnerContentId
        : undefined,
      session.contentId,
      session.partnerContentId,
      ...actionKernelContentIds
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== "string" || candidate.trim() === "") {
        continue;
      }

      const decoded: string = (() => {
        try {
          return decodeURIComponent(candidate.trim());
        } catch {
          return candidate.trim();
        }
      })();

      if (CONTENT_ID_PATTERN.test(decoded)) {
        this.log(
          "debug",
          "getCurrentContentId(): resolved contentId " + decoded
        );
        return decoded;
      }
    }

    this.log(
      "warn",
      "getCurrentContentId(): unable to resolve valid contentId"
    );
    return null;
  }

  /**
   * Returns the ISO-8601 start time of the current airing offer.
   * Used to populate `timeAndChannel.dateTime` in the recording POST body.
   */
  public getAiringStartTime(): string | null {
    const metadata = this.getMetadata();
    const airingOffers = metadata.airingOffers;
    if (!airingOffers || airingOffers.length === 0) {
      return null;
    }
    const offer = airingOffers[0] as { startTime?: string };
    return offer.startTime === undefined ? null : offer.startTime;
  }

  public getMetadataError(): PlaybackError {
    return this.mMetadataManager.getMetadataError();
  }

  public getMetadataState(): MetadataState {
    return this.mMetadataManager.getMetadataState();
  }

  /**
   * Whether a metadata `*_ERROR` state blocks playback for the currently
   * playing content type, vs. one playback silently continues through.
   */
  public isMetadataErrorBlocking(state: MetadataState): boolean {
    if (this.mPlayable === null) {
      return false;
    }
    return isBlockingMetadataError(state, this.mPlayable.playbackContentType);
  }

  public prepareMetadata(playable: IPlayable): void {
    this.mMetadataManager.prepareMetadata(playable);
  }

  public prepareMetadataByType(
    playable: IPlayable,
    metadataType: MetadataType,
    playerCurrentTime?: number
  ): void {
    this.log("debug", "prepareMetadataByType() - " + metadataType);
    this.mMetadataManager.prepareMetadataByType(
      playable,
      metadataType,
      playerCurrentTime
    );
  }

  /**
   * Re-fetches the action list for the live program already loaded, so the
   * player shows actions that match recordings changed while it was away.
   *
   * A recording or OnePass mutation clears ActionsService's LRU cache, but
   * returning to the same program resumes it rather than re-preparing its
   * metadata, so the list loaded before the change would otherwise stay. An
   * untouched cache answers this without a network call.
   *
   */
  public refreshLiveActions(): void {
    const playable = this.mPlayable;
    if (
      playable === null ||
      playable.playbackContentType !== PlaybackContentType.LIVE ||
      this.getMetadata().actions === undefined
    ) {
      return;
    }
    this.prepareMetadataByType(playable, MetadataType.ACTION_LIST);
  }

  // ── RestrictionManager Delegations ───────────────────────────────────────

  public checkAllRestriction(
    playable: IPlayable,
    metadata: IMetadata
  ): Promise<PlaybackRestrictionType> {
    return this.mRestrictionManager.checkAllRestriction(playable, metadata);
  }

  public checkCanPlayStartRestriction(
    playable: IPlayable
  ): Promise<PlaybackRestrictionType> {
    return this.mRestrictionManager.checkCanPlayStartRestriction(playable);
  }

  // ── SessionManager Delegations ───────────────────────────────────────────

  public getSession(): Record<string, unknown> {
    return this.mSessionManager.getSession() as unknown as Record<
      string,
      unknown
    >;
  }

  /**
   * The session that answered for the current content, or `null` while it
   * has not. Unlike getSession(), never the previous content's session.
   */
  public getActiveSession(): Session | null {
    return this.mActiveSession;
  }

  public getSessionError(): IPlaybackError {
    return this.mSessionManager.getSessionError();
  }

  public getSessionState(): SessionState {
    return this.mSessionManager.getSessionState();
  }

  // ── VideoManager Delegations ─────────────────────────────────────────────

  public getPlayerCurrentTime(): number {
    return this.mVideoManager.getPlayerCurrentTime();
  }

  public getSeekRange(): TSeekRange {
    return this.mVideoManager.getSeekableRange();
  }

  public getStreamingUrl(): string | null {
    return this.mVideoManager.getStreamingUrl();
  }

  public getTransportEncodingType(): string | null {
    return this.mVideoManager.getTransportEncodingType();
  }

  public getDrmServerType(): string | null {
    const drmType = this.mVideoManager.getDrmServerType();
    return drmType !== null ? String(drmType) : null;
  }

  public getPlayerStats(): TPlayerStats {
    return this.mVideoManager.getStats();
  }

  /**
   * Manifest URI the player actually loaded, after redirects and CDN
   * routing — not the URL the session handed to the player. Falls back to
   * the session URL when the player has nothing loaded.
   *
   * @returns The resolved manifest URI, or null when neither is known.
   */
  public getResolvedStreamingUrl(): string | null {
    const assetUri: string | null = this.mVideoManager.getAssetUri();
    if (assetUri !== null && assetUri.length > 0) {
      return assetUri;
    }
    return this.mVideoManager.getStreamingUrl();
  }

  /**
   * Seconds of contiguous buffered media ahead of the playhead.
   *
   * @returns Seconds buffered ahead; 0 when nothing is loaded.
   */
  public getBufferAheadSeconds(): number {
    return this.mVideoManager.getBufferAheadSeconds();
  }

  /**
   * DRM parameters the CDM negotiated for the loaded source. Diagnostics
   * only — reflects what the platform granted, not what was requested.
   *
   * @returns Negotiated DRM info, or null when unencrypted / not loaded.
   */
  public getActiveDrmInfo(): TActiveDrmInfo | null {
    return this.mVideoManager.getActiveDrmInfo();
  }

  /**
   * Current EME key statuses keyed by key id. Empty when no keys are
   * tracked.
   *
   * @returns Map of key id to `MediaKeyStatus` string.
   */
  public getKeyStatuses(): Record<string, string> {
    return this.mVideoManager.getKeyStatuses();
  }

  public getPlayerViewMode(): PlayerViewMode {
    return this.mVideoManager.getPlayerViewMode();
  }

  // ── Audio Track Control ──────────────────────────────────────────────────

  private storeSelectedAudioTrackPreference(track: TAudioTrack): void {
    let isAudioDescription: boolean = false;
    if (
      track.kind === "ad" ||
      track.roles.includes("description") ||
      track.roles.includes("describes-video") ||
      /audio description|descriptive audio|\bAD\b/i.test(track.label || "")
    ) {
      isAudioDescription = true;
    } else {
      StorageAdapter.getInstance()
        .delete(STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION)
        .catch((error: unknown): void => {
          Log.e("error", STORE_SELECTED_AUDIO_TRACK_PREFERENCE_METHOD, error);
        });
    }
    const audioTrackPreference: TAudioTrackPreference = {
      language: track.language,
      codec:
        track.codec !== null && track.codec !== undefined ? track.codec : null
    };

    const storageKeyName: string = isAudioDescription
      ? STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION
      : STORAGE_KEY_USER_PREFERRED_AUDIO;

    try {
      StorageAdapter.getInstance()
        .set(storageKeyName, audioTrackPreference, Number.POSITIVE_INFINITY)
        .catch((error: unknown): void => {
          Log.e(
            VideoPlaybackController.TAG,
            STORE_SELECTED_AUDIO_TRACK_PREFERENCE_METHOD,
            error
          );
        });
    } catch (error: unknown) {
      Log.e("error", STORE_SELECTED_AUDIO_TRACK_PREFERENCE_METHOD, error);
    }
  }

  private async getUserPreferedAudioDescriptionTrack(
    tracks: TAudioTrack[]
  ): Promise<TAudioTrack | null> {
    const storedPreferenceRaw = await StorageAdapter.getInstance().get(
      STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION
    );
    const storedPreference =
      this.mTrackPrefereneceUtils.parseAudioTrackPreference(
        storedPreferenceRaw
      );
    if (storedPreference !== null) {
      return this.mTrackPrefereneceUtils.getAvailableAudioDescriptionTrack(
        tracks,
        storedPreference
      );
    }
    return null;
  }

  private async getUserPreferedAudioTrack(
    tracks: TAudioTrack[]
  ): Promise<TAudioTrack | null> {
    const storedPreferenceRaw = await StorageAdapter.getInstance().get(
      STORAGE_KEY_USER_PREFERRED_AUDIO
    );
    const storedPreference =
      this.mTrackPrefereneceUtils.parseAudioTrackPreference(
        storedPreferenceRaw
      );
    if (storedPreference !== null) {
      return this.mTrackPrefereneceUtils.resolveTrackFromStoredPreference(
        tracks,
        storedPreference
      );
    }
    return null;
  }

  private async applyStoredOrDefaultAudioTrackPreference(
    tracks: TAudioTrack[]
  ): Promise<void> {
    let preferredTrack: TAudioTrack | null = null;

    try {
      const isAudioDescriptionSelected: boolean =
        await StorageAdapter.getInstance().has(
          STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION
        );

      if (isAudioDescriptionSelected) {
        preferredTrack =
          await this.getUserPreferedAudioDescriptionTrack(tracks);
      }
      if (preferredTrack === null) {
        preferredTrack = await this.getUserPreferedAudioTrack(tracks);
      }
    } catch (error: unknown) {
      this.log("error", "applyStoredOrDefaultAudioTrackPreference()", error);
    }
    if (preferredTrack === null) {
      preferredTrack =
        this.mTrackPrefereneceUtils.resolvePreferredAudioTrack(tracks);
    }

    const selectedAudioTrack = this.mVideoManager.getSelectedAudioTrack();
    if (
      selectedAudioTrack === null ||
      selectedAudioTrack.id !== preferredTrack.id
    ) {
      this.mVideoManager.setAudioTrack(preferredTrack.id);
    }
  }

  private handleAudioPreference(): void {
    const tracks = this.mVideoManager.getAudioTracks();
    if (tracks.length === 0) {
      return;
    }

    this.applyStoredOrDefaultAudioTrackPreference(tracks);
  }

  private async handleTextPreference(): Promise<void> {
    let isEnabledRaw: unknown = null;
    try {
      isEnabledRaw = await StorageAdapter.getInstance().get(
        STORAGE_KEY_IS_TEXT_TRACK_ENABLED
      );
    } catch (error: unknown) {
      this.log("error", "handleTextPreference()", error);
      return;
    }
    if (isEnabledRaw !== true) {
      if (isEnabledRaw === null) {
        // First boot: seed the persisted flag so closed captions are
        // explicitly recorded as disabled (no preferred language stored yet).
        this.storeTextTrackEnabled(false);
      }
      return;
    }
    const tracks = this.mVideoManager.getTextTracks();
    if (tracks.length === 0) {
      return;
    }

    this.applyStoredOrDefaultTextTrackPreference(tracks);
  }

  private storeTextTrackEnabled(enabled: boolean): void {
    try {
      StorageAdapter.getInstance()
        .set(
          STORAGE_KEY_IS_TEXT_TRACK_ENABLED,
          enabled,
          Number.POSITIVE_INFINITY
        )
        .catch((error: unknown): void => {
          this.log("error", "storeTextTrackEnabled()", error);
        });
    } catch (error: unknown) {
      this.log("error", "storeTextTrackEnabled()", error);
    }
  }

  public getAudioTracks(): TAudioTrack[] {
    const tracks = this.mVideoManager.getAudioTracks();
    return tracks;
  }

  /**
   * Returns the available audio tracks as serializable display options
   * (id + human-readable label), suitable for rendering in a selection UI.
   *
   * @returns Array of { id, label } in track order.
   */
  public getAudioTrackOptions(): { id: number; label: string }[] {
    if (this.mAudioTracksMap.length !== 0) {
      return this.mAudioTracksMap;
    }
    const tracks = this.mVideoManager.getAudioTracks();
    const tracksMap = new TrackUtils().getAudioTrackMap(tracks);
    const options: { id: number; label: string }[] = [];
    for (const [id, label] of tracksMap) {
      options.push({ id, label });
    }
    this.mAudioTracksMap = options;
    return this.mAudioTracksMap;
  }

  public setAudioTrack(audioId: number): void {
    let selectedTrack = this.mVideoManager.getSelectedAudioTrack();
    if (selectedTrack && selectedTrack.id === audioId) {
      return;
    }
    this.mVideoManager.setAudioTrack(audioId);

    selectedTrack = this.mVideoManager.getSelectedAudioTrack();
    if (selectedTrack !== null) {
      this.storeSelectedAudioTrackPreference(selectedTrack);
    }
  }

  public setAudioTrackByLanguage(language: string): void {
    let selectedTrack = this.mVideoManager.getSelectedAudioTrack();
    if (selectedTrack && selectedTrack.language === language) {
      return;
    }
    this.mVideoManager.setAudioTrackByLanguage(language);

    selectedTrack = this.mVideoManager.getSelectedAudioTrack();
    if (selectedTrack !== null) {
      this.storeSelectedAudioTrackPreference(selectedTrack);
    }
  }

  public getSelectedAudioTrack(): TAudioTrack | null {
    return this.mVideoManager.getSelectedAudioTrack();
  }

  // ── Text Track Control ───────────────────────────────────────────────────

  public getTextTracks(): TTextTrack[] {
    this.mTextTracks = this.mVideoManager.getTextTracks();
    return this.mTextTracks;
  }

  public getTextTrackOptions(): { id: number; label: string }[] {
    const tracks = this.mVideoManager.getTextTracks();
    const tracksMap = new TrackUtils().getTextTrackMap(tracks);
    const options: { id: number; label: string }[] = [];
    for (const [id, label] of tracksMap) {
      options.push({ id, label });
    }
    return options;
  }

  public getSelectedTextTrack(): TTextTrack | null {
    return this.mVideoManager.getSelectedTextTrack();
  }

  /**
   * Apply the given text track and persist it as the user's preferred
   * closed-caption selection.
   *
   * @param track - The text track to select and store.
   */
  public setTextTrackById(trackId: number): void {
    const textTrack = this.mVideoManager
      .getTextTracks()
      .find((track) => track.id === trackId);
    if (!textTrack) {
      return;
    }

    this.mVideoManager.setTextTrack(textTrack.id);
    this.storeSelectedTextTrackPreference(textTrack);
  }

  public isTextTrackSupported(): boolean {
    return this.mVideoManager.isTextTrackSupported();
  }

  public isTextTrackChangeSupported(): boolean {
    return this.mVideoManager.isTextTrackChangeSupported();
  }

  public isClosedCaptionActive(): boolean {
    return this.mVideoManager.isClosedCaptionActive();
  }

  public toggleClosedCaption(): void {
    if (!this.isTextTrackSupported()) {
      return;
    }

    const isClosedCaptionActive = this.isClosedCaptionActive();
    if (isClosedCaptionActive) {
      this.mVideoManager.setTextTrackVisibility(false);
      this.storeTextTrackEnabled(false);
      return;
    }

    this.mVideoManager.setTextTrackVisibility(true);
    this.storeTextTrackEnabled(true);

    const selectedTrack = this.getSelectedTextTrack();
    if (selectedTrack !== null) {
      this.mVideoManager.setTextTrack(selectedTrack.id);
      return;
    }

    const tracks = this.getTextTracks();
    if (tracks.length === 0) {
      return;
    }

    this.applyStoredOrDefaultTextTrackPreference(tracks);
  }

  /**
   * Enable closed captions and select the given text track. Persists both the
   * selected track as the user's preferred closed-caption selection and the
   * enabled flag so the choice is restored on the next playback session.
   *
   * @param trackId - The id of the text track to enable and select.
   */
  public enableClosedCaptionById(trackId: number): void {
    if (!this.isTextTrackSupported()) {
      return;
    }

    this.mVideoManager.setTextTrackVisibility(true);
    this.setTextTrackById(trackId);
    this.storeTextTrackEnabled(true);
  }

  /**
   * Disable closed captions (hide text tracks) and persist the disabled flag.
   * The user's preferred closed-caption selection is intentionally retained in
   * storage so it can be restored when captions are re-enabled.
   */
  public disableClosedCaption(): void {
    if (!this.isTextTrackSupported()) {
      return;
    }

    this.mVideoManager.setTextTrackVisibility(false);
    this.storeTextTrackEnabled(false);
  }

  // ── Closed-Caption Preference ────────────────────────────────────────────

  private storeSelectedTextTrackPreference(track: TTextTrack): void {
    const textTrackPreference: TTextTrackPreference = {
      id: track.id,
      language: track.language,
      kind: track.kind !== null && track.kind !== undefined ? track.kind : null
    };

    try {
      StorageAdapter.getInstance()
        .set(
          STORAGE_KEY_USER_PREFERRED_CC,
          textTrackPreference,
          Number.POSITIVE_INFINITY
        )
        .catch((error: unknown): void => {
          this.log("error", "storeSelectedTextTrackPreference()", error);
        });
    } catch (error: unknown) {
      this.log("error", "storeSelectedTextTrackPreference()", error);
    }
  }

  private async applyStoredOrDefaultTextTrackPreference(
    tracks: TTextTrack[]
  ): Promise<void> {
    let preferredTrack: TTextTrack | null = null;

    try {
      const storedPreferenceRaw = await StorageAdapter.getInstance().get(
        STORAGE_KEY_USER_PREFERRED_CC
      );
      const storedPreference =
        this.mTrackPrefereneceUtils.parseTextTrackPreference(
          storedPreferenceRaw
        );
      if (storedPreference !== null) {
        preferredTrack =
          this.mTrackPrefereneceUtils.resolveTrackFromStoredTextPreference(
            tracks,
            storedPreference
          );
      }
    } catch (error: unknown) {
      this.log("error", "applyStoredOrDefaultTextTrackPreference()", error);
    }

    if (preferredTrack === null) {
      preferredTrack =
        this.mTrackPrefereneceUtils.resolvePreferredTextTrack(tracks);
    }

    this.setTextTrackById(preferredTrack.id);
  }

  // ── LVP & Activity Logging ──────────────────────────────────────────────

  /**
   * The body of a watch-history last-viewed-position save, built from a watch
   * action's kernel, or `undefined` when there is nothing worth saving (no
   * current time, or no offer id/`contentId` to file it under).
   *
   * `offerIdKey` names the kernel field sent as `offerId` — the key the
   * watch-history service files the position under, and that action details
   * look it up by on the next launch. It differs by content type (verified
   * against integration): catch-up is keyed by TiVo's offer id
   * (`tivo:of.ts.…`), VOD by the partner's (`partnerOfferId`). A position
   * saved under the other id is accepted but never read back.
   * `percentWatched` is derived from the position and duration, capped at 100.
   */
  private static toWatchHistoryLastViewedPosition(
    kernel: Record<string, unknown> | undefined,
    offerIdKey: "offerId" | "partnerOfferId",
    currentTime: number
  ): LastViewedPosition | undefined {
    if (kernel === undefined || !currentTime) {
      return undefined;
    }
    const { partnerId, contentId, collectionId, duration } = kernel;
    const offerId: unknown = kernel[offerIdKey];
    if (
      typeof offerId !== "string" ||
      offerId === "" ||
      typeof contentId !== "string" ||
      contentId === ""
    ) {
      return undefined;
    }

    const lastViewedPosition = Math.round(currentTime);
    const body: LastViewedPosition = {
      offerId,
      partnerId: typeof partnerId === "string" ? partnerId : "",
      contentId,
      lastViewedPosition
    };
    if (typeof collectionId === "string" && collectionId !== "") {
      body.collectionId = collectionId;
    }
    if (typeof duration === "number" && duration > 0) {
      body.duration = duration;
      body.percentWatched = Math.min(
        100,
        Math.round((lastViewedPosition / duration) * 100)
      );
    }
    return body;
  }

  /**
   * Saves the catch-up resume point to the watch-history service
   * (POST /v1/watch-history/socu/last-viewed-position), which is what feeds
   * `savedPosition` back into the `watchFromSoCuAction` kernel on the next
   * launch.
   */
  private updateSocuLastViewedPosition(currentTime: number): void {
    try {
      const body = VideoPlaybackController.toWatchHistoryLastViewedPosition(
        getActionKernel(this.getMetadata().watchAction),
        "offerId",
        currentTime
      );
      if (body === undefined) {
        return;
      }
      // Survives the exit navigation that immediately follows a live-tv switch.
      new WatchHistoryInteractor()
        .saveSocuLastViewedPosition(body, {
          bypassNavigationCancellation: true
        })
        .catch((error: unknown): void => {
          this.log("error", "saveSocuLastViewedPosition()", error);
        });
    } catch (error) {
      this.log("error", "error", error);
      throw error;
    }
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](VideoPlaybackController.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      VideoPlaybackController.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        VideoPlaybackController.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  private updateNdvrLastViewedPosition(): void {
    const recordingId: string = this.getNdvrRecordingId();
    const currentTime = this.getPlayerCurrentTime();
    let lastViewedPosition: number = Math.round(currentTime);
    if (this.isNdvrLive()) {
      lastViewedPosition = Math.round(currentTime - this.getSeekRange().start);
    }
    try {
      // Survives the exit navigation that immediately follows a live-tv switch.
      new RecordingsServiceInteractor()
        .updateLastViewedPosition(
          {
            recordingId,
            lastViewedPosition: { lastViewedPosition }
          } as TRecordingsUpdateLastViewedPositionParams,
          { bypassNavigationCancellation: true }
        )
        .catch((error: unknown): void => {
          this.log("error", "updateLastViewedPosition()", error);
        });
    } catch (error) {
      this.log("error", "error", error);
      throw error;
    }
  }

  /**
   * Saves a VOD title's resume point to the watch-history service
   * (POST /v1/watch-history/vod/last-viewed-position), which is what feeds
   * `savedPosition` back into the `watchFromVodAction` kernel on the next
   * launch. Keyed by the partner's offer id — see
   * toWatchHistoryLastViewedPosition().
   */
  private updateVodLastViewedPosition(currentTime: number): void {
    try {
      const body = VideoPlaybackController.toWatchHistoryLastViewedPosition(
        getActionKernel(this.getMetadata().watchVodAction),
        "partnerOfferId",
        currentTime
      );
      if (body === undefined) {
        return;
      }
      // Survives the exit navigation that immediately follows a live-tv switch.
      new WatchHistoryInteractor()
        .saveVodLastViewedPosition(body, {
          bypassNavigationCancellation: true
        })
        .catch((error: unknown): void => {
          this.log("error", "saveVodLastViewedPosition()", error);
        });
    } catch (error) {
      this.log("error", "error", error);
      throw error;
    }
  }

  /** Whether the VOD being played is a preview (trailer). */
  private isVodPreview(): boolean {
    return (
      this.mPlayable !== null &&
      this.mPlayable.vodPlaybackModel !== undefined &&
      this.mPlayable.vodPlaybackModel.isPreview === true
    );
  }

  /**
   * Where a VOD title starts, in seconds: its saved position when resuming,
   * otherwise `undefined` (the start).
   *
   * A start-over request and a preview always start from the beginning. A
   * saved position at or past the end is treated as finished rather than
   * resumed into the last frame, matching Kaveri's pause-point check.
   */
  private getVodStartPosition(): number | undefined {
    if (this.mPlayable === null) {
      return undefined;
    }
    const vodModel = this.mPlayable.vodPlaybackModel;
    if (vodModel === undefined || vodModel.isStartOver === true) {
      return undefined;
    }
    if (this.isVodPreview()) {
      return undefined;
    }

    const watchVodAction = this.getMetadata().watchVodAction;
    const kernel =
      watchVodAction !== undefined ? watchVodAction.kernel : undefined;
    if (kernel === undefined) {
      return undefined;
    }

    return VideoPlaybackController.toResumePosition(
      kernel.savedPosition,
      kernel.duration
    );
  }

  /**
   * Where a catch-up airing starts, in seconds: the saved position from its
   * `watchFromSoCuAction` kernel when resuming, otherwise `undefined` (the
   * start).
   *
   * Only CATCHUP_RESUME resumes. CATCHUP_BEGINNING is an explicit request for
   * the start, and Start Over is left alone: it saves a position relative to
   * the airing window, not to the stream, so it cannot be seeked to as is.
   */
  private getCatchUpStartPosition(): number | undefined {
    if (
      this.mPlayable === null ||
      this.mPlayable.playbackContentType !== PlaybackContentType.CATCHUP_RESUME
    ) {
      return undefined;
    }

    const kernel = getActionKernel(this.getMetadata().watchAction);
    if (kernel === undefined) {
      return undefined;
    }

    return VideoPlaybackController.toResumePosition(
      kernel.savedPosition,
      kernel.duration
    );
  }

  /**
   * Where the current playable starts, in seconds, or `undefined` for the
   * stream's default start. Passed to the player at load time, so a resumed
   * title opens at its saved position rather than seeking after the fact.
   */
  private getStartPosition(): number | undefined {
    if (this.isVod()) {
      return this.getVodStartPosition();
    }
    if (this.isCatchUp()) {
      return this.getCatchUpStartPosition();
    }
    if (this.isNdvrRecorded()) {
      return this.getNdvrStartPosition();
    }
    return undefined;
  }

  /**
   * Where a completed recording starts, in seconds: the last viewed position
   * the recordings service returned for it, otherwise `undefined` (the start).
   *
   * An in-progress recording is left alone: it saves its position relative to
   * the seekable window's start (see updateNdvrLastViewedPosition()), which is
   * only known once the stream has loaded, so it cannot be a load-time start
   * position.
   */
  private getNdvrStartPosition(): number | undefined {
    const metadata = this.getMetadata();
    const kernel = getActionKernel(metadata.watchAction);
    return VideoPlaybackController.toResumePosition(
      metadata.ndvrLastViewedPosition,
      kernel !== undefined ? kernel.duration : undefined
    );
  }

  /**
   * A saved position worth resuming from, or `undefined` to start from the
   * beginning. Nothing saved (zero, missing, not a number) starts from the
   * beginning, and so does a position at or past the end — treated as
   * finished rather than resumed into the last frame, matching Kaveri's
   * pause-point check.
   */
  private static toResumePosition(
    savedPosition: unknown,
    duration: unknown
  ): number | undefined {
    if (
      typeof savedPosition !== "number" ||
      !Number.isFinite(savedPosition) ||
      savedPosition <= 0
    ) {
      return undefined;
    }
    if (typeof duration === "number" && savedPosition >= duration) {
      return undefined;
    }
    return savedPosition;
  }

  public updateLastViewedPosition(): void {
    const currentTime = this.getPlayerCurrentTime();
    if (this.isSocu()) {
      this.updateSocuLastViewedPosition(currentTime);
    }
    if (this.isNdvr()) {
      this.updateNdvrLastViewedPosition();
    }
    // A preview's position is not the title's: saving it would make the full
    // title resume partway through the trailer's running time.
    if (this.isVod() && !this.isVodPreview()) {
      this.updateVodLastViewedPosition(currentTime);
    }
    this.log("debug", "updateLastViewedPosition() - stub");
  }

  private setLastViewedPositionInterval(): void {
    this.clearLastViewedPositionInterval();
    this.mLVPIntervalId = setInterval(() => {
      if (!this.isLive()) {
        this.updateLastViewedPosition();
      }
    }, LAST_VIEWED_POSITION_INTERVAL_TIME);
  }

  private clearLastViewedPositionInterval(): void {
    if (this.mLVPIntervalId !== null) {
      clearInterval(this.mLVPIntervalId);
      this.mLVPIntervalId = null;
    }
  }

  // ── Live Auto-Resume Timer ────────────────────────────────────────────

  /**
   * Schedules a one-shot timer, while paused on LIVE content, for the
   * moment the DVR window start is expected to reach the paused position
   * (i.e. the pause point falls out of the timeshift buffer). Mirrors
   * mobi's MediaController#startAutoResumeTimer: compute the remaining
   * time directly instead of polling, and auto-resume once it elapses.
   */
  private scheduleLiveResumeTimer(): void {
    this.clearLiveResumeTimer();
    if (!this.isLive()) {
      return;
    }
    if (this.mIsLiveAutoResumeSuppressed) {
      this.log(
        "debug",
        "scheduleLiveResumeTimer() - skipped (auto-resume suppressed)"
      );
      return;
    }
    if (
      !this.mIsForcedPaused ||
      this.mPlayerError !== null ||
      !this.mIsNetworkConnected
    ) {
      this.log(
        "debug",
        "scheduleLiveResumeTimer() - skipped (forced pause, error, or offline)"
      );
      return;
    }

    const seekRange = this.getSeekRange();
    const currentPosition = this.getPlayerCurrentTime();
    if (seekRange.end <= seekRange.start) {
      this.log(
        "debug",
        "scheduleLiveResumeTimer() - skipped (invalid seek range)"
      );
      return;
    }
    const timeshiftWindowSeconds = seekRange.end - seekRange.start;
    const durationBehindLive = seekRange.end - currentPosition;
    const remainingSeconds = Math.max(
      0,
      timeshiftWindowSeconds - durationBehindLive
    );
    const delayMs = Math.max(
      VideoPlaybackController.LIVE_RESUME_MIN_DELAY_MS,
      remainingSeconds * 1000
    );

    this.log("debug", "scheduleLiveResumeTimer() - delayMs: " + delayMs);

    this.mLiveResumeTimerId = setTimeout(() => {
      this.mLiveResumeTimerId = null;
      if (
        this.isLive() &&
        this.mVideoPlayerCurrentState === PlayerState.PAUSED
      ) {
        this.log("debug", "scheduleLiveResumeTimer() - auto-resuming");
        this.resume();
      }
    }, delayMs);
  }

  /**
   * Suppresses or re-enables the live auto-resume timer.
   *
   * A forced pause on live content arms a timer that resumes playback once
   * the pause point falls out of the timeshift buffer. That is right for a
   * viewer's own pause and wrong while an Emergency Alert System alert holds
   * the player: a viewer already far behind live would have the programme
   * resume underneath the overlay, and the auto-resume would clear the forced
   * flag the alert is relying on.
   *
   * Suppressing cancels any timer already armed, so it is safe to call after
   * the pause that armed it.
   *
   * @param isSuppressed - True to cancel and inhibit the timer, false to
   *   allow it again
   */
  public setLiveAutoResumeSuppressed(isSuppressed: boolean): void {
    this.mIsLiveAutoResumeSuppressed = isSuppressed;
    if (isSuppressed) {
      this.clearLiveResumeTimer();
    }
    this.log(
      "debug",
      "setLiveAutoResumeSuppressed() - " + String(isSuppressed)
    );
  }

  /** Clears the pending live auto-resume timer, if any. */
  private clearLiveResumeTimer(): void {
    if (this.mLiveResumeTimerId !== null) {
      clearTimeout(this.mLiveResumeTimerId);
      this.mLiveResumeTimerId = null;
    }
  }

  // ── Misc ─────────────────────────────────────────────────────────────────

  private setPlayable(playable: IPlayable): void {
    if (
      playable.playbackContentType === PlaybackContentType.LIVE &&
      this.mPlayable !== null &&
      this.mPlayable.channel !== undefined &&
      playable.channel !== undefined &&
      this.mPlayable.channel.channelId === playable.channel.channelId &&
      this.mPlayable.playbackContentType === playable.playbackContentType
    ) {
      return;
    }
    this.mPlayable = playable;
    this.mPlayableChangeSubscription.notify(playable);
  }

  /**
   * Switch the current playable to a new playback content type.
   *
   * Switching INTO Start Over (STARTOVER_BEGINNING / STARTOVER_RESUME) runs the
   * full playback flow via startPlayback() — metadata, restriction, and a fresh
   * session. Other transitions (e.g. Go LIVE) reuse the session via the
   * initPlayback() shortcut / switchToLiveTv().
   *
   * @param contentType - The target PlaybackContentType.
   * @param resumeBasePositionSeconds - For STARTOVER_RESUME only: the absolute
   *   position (epoch seconds) to derive the resume offset from. Callers pass the
   *   scrub target when scrubbing (the playhead has not moved to it yet); when
   *   omitted the player's current time is used.
   */
  public switchPlaybackContentType(
    contentType: PlaybackContentType,
    resumeBasePositionSeconds?: number
  ): void {
    this.log("info", "switchPlaybackContentType() - " + contentType);
    if (this.mPlayable === null) {
      return;
    }

    // Capture the resume offset BEFORE any teardown — startPlayback() → stop()
    // clears the current time. Only STARTOVER_RESUME resumes at an equivalent
    // position; STARTOVER_BEGINNING starts fresh.
    //
    // Scrubbing is a UI-only preview: the playhead does not move to the scrub
    // target until commit, so the caller passes the scrub target position via
    // `resumeBasePositionSeconds`. For skip / long-press / FF-RW the playhead
    // already tracks the target, so the base defaults to the player time.
    if (contentType === PlaybackContentType.STARTOVER_RESUME) {
      this.mStartOverResumeSeconds = this.computeStartOverResumeSeconds(
        resumeBasePositionSeconds
      );
    } else if (contentType === PlaybackContentType.STARTOVER_BEGINNING) {
      this.mStartOverResumeSeconds = null;
    }

    const session = this.mSessionManager.getSession();
    this.applyTrickPlayRestrictionsFromSession(session);
    this.mPlayable = {
      ...this.mPlayable,
      playbackContentType: contentType
    };
    this.mPlayableChangeSubscription.notify(this.mPlayable);

    // Switching INTO Start Over must run the full playback flow (metadata →
    // restriction → fresh session) via startPlayback(). The initPlayback()
    // session-reuse shortcut is only for same-session transitions (e.g. Go LIVE).
    if (
      contentType === PlaybackContentType.STARTOVER_BEGINNING ||
      contentType === PlaybackContentType.STARTOVER_RESUME
    ) {
      const metadata = this.getMetadata();
      const airingOffers = metadata.airingOffers;
      let offerId: string = "";

      if (
        airingOffers !== undefined &&
        airingOffers !== null &&
        airingOffers.length > 0
      ) {
        offerId = airingOffers[0]["offerId"] as string;
      }
      this.mPlayable = { ...this.mPlayable, offerId: offerId };
      this.startPlayback(this.mPlayable);
    } else {
      this.switchToLiveTv();
    }
  }

  /**
   * Current programme's start time (epoch seconds), or 0 when unavailable.
   */
  private getProgramStartEpoch(): number {
    const metadata = this.mMetadataManager.getMetadata();
    return new AiringPreviewUtils(
      metadata.airingOffers as Array<Record<string, unknown>> | undefined,
      metadata.offerPreview
    ).getStartTimeEpoch();
  }

  /**
   * Compute the Start Over resume position in seconds from the start of the
   * Start Over stream, derived from a base playback position and the programme
   * start time. Returns 0 when the programme start epoch is unavailable.
   *
   * The base position and the airing start time are both absolute epoch
   * seconds, so the offset into the equivalent Start Over stream position is
   * `basePosition − programmeStartEpoch`, clamped to `>= 0`.
   *
   * @param basePositionSeconds - Absolute position (epoch seconds) to resume
   *   from. Callers pass the scrub target for scrubbing (the playhead has not
   *   moved to it yet); when omitted it defaults to the player's current time
   *   (correct for skip / long-press / FF-RW, where the playhead tracks the
   *   target).
   */
  private computeStartOverResumeSeconds(basePositionSeconds?: number): number {
    const programStartEpoch = this.getProgramStartEpoch();
    if (programStartEpoch <= 0) {
      return 0;
    }
    const basePosition =
      basePositionSeconds === undefined
        ? this.mVideoManager.getPlayerCurrentTime()
        : basePositionSeconds;
    return Math.max(0, basePosition - programStartEpoch);
  }

  /**
   * Backward seek limit (epoch seconds) that lets the user seek past the
   * live buffer start directly into the Start Over stream, instead of the
   * seek motion stopping dead at the buffer start pending confirmation.
   * Returns null when the extension does not apply — not currently on Live
   * (covers VOD/NDVR/Start Over/Catch Up alike), no dedicated Start Over
   * stream for this airing, or the programme start time is unknown.
   */
  private getStartOverLowerBoundEpoch(): number | null {
    if (!this.isLive() || !this.mStartOverAvailable) {
      return null;
    }
    const programStartEpoch = this.getProgramStartEpoch();
    return programStartEpoch > 0 ? programStartEpoch : null;
  }

  /**
   * Push the current Start Over backward seek limit to the active
   * TrickPlayManager, if one exists. Called whenever trick-play eligibility
   * is (re)applied so the limit stays in sync with session/airing changes.
   */
  private pushStartOverLowerBound(): void {
    const tpm = this.mVideoManager.getTrickPlayManager();
    if (tpm !== null && typeof tpm.setStartOverLowerBound === "function") {
      tpm.setStartOverLowerBound(this.getStartOverLowerBoundEpoch());
    }
  }

  public async switchToLiveTv(): Promise<void> {
    this.log("debug", "switchToLiveTv()");
    // An unloaded player reports position 0, so saving now would overwrite
    // the real resume point — e.g. the one full-screen end of stream saves
    // before it stops the player.
    if (this.mIsPlayerLoaded) {
      this.updateLastViewedPosition();
    }

    // A hidden player has no surface, so it must not re-tune itself into
    // playing. Drop the stale stream and keep the deferred resume intent —
    // restoreFromHidden() re-acquires a source when a screen with a player is
    // next opened. Reachable via onEndOfStream(); the user-driven callers all
    // run from /player, where the view mode is never Hidden.
    if (this.mPlayerViewMode === PlayerViewMode.Hidden) {
      this.log("info", "switchToLiveTv() - skipped, player is Hidden");
      this.mWasPlayingBeforeHidden = true;
      this.stop();
      return;
    }

    if (
      this.isSocu() &&
      this.mPlayable !== null &&
      this.mPlayable.channel !== undefined
    ) {
      VideoPlaybackController.getInstance().startPlayback({
        channel: this.mPlayable.channel,
        playbackContentType: PlaybackContentType.LIVE
      });
      return;
    }

    const lastChannelNumber = await DeviceModel.getLastTunedChannelNumber();
    const channelNumber =
      lastChannelNumber === undefined || lastChannelNumber === null
        ? "1"
        : lastChannelNumber;
    const channel = await new ChannelsInteractor().tuneToChannel(channelNumber);
    this.startPlayback({
      channel,
      playbackContentType: PlaybackContentType.LIVE
    });
  }

  /**
   * The channel number currently playing, as channel-list navigation expects
   * it.
   *
   * Prefers the `channelNumber` the channels response supplies. The fallback
   * derives it from the trailing digits of `channelId`, which only holds when
   * a provider happens to end its ids with the channel number — for any other
   * id shape it yields the wrong number, or an empty string that silently
   * stops channel change before it starts.
   *
   * @returns The channel number, or `""` when neither source provides one.
   */
  public getCurrentChannelNumber(): string {
    if (this.mPlayable === null || this.mPlayable.channel === undefined) {
      return "";
    }

    const { channelNumber, channelId } = this.mPlayable.channel;

    if (
      channelNumber !== undefined &&
      channelNumber !== null &&
      channelNumber !== ""
    ) {
      return channelNumber;
    }

    if (channelId) {
      // eslint-disable-next-line sonarjs/slow-regex
      const match = channelId.match(/(\d+)$/);
      return match ? match[1] : "";
    }

    return "";
  }

  public setIsAntiburnActive(isAntiburnActive: boolean): void {
    this.mIsAntiburnActive = isAntiburnActive;
  }

  public isUnsupportedContent(): boolean {
    // TODO: Phase 8.11 — Check UHD/PPV restrictions
    return false;
  }

  public updateCurrentPlayPosition(): void {
    // TODO: Phase 8.10 — Update play position based on content type
    this.log("debug", "updateCurrentPlayPosition() - stub");
  }

  public getDuration(): number {
    return this.mVideoManager.getDuration();
  }

  public getVideoVariants(): unknown[] {
    return this.mVideoManager.getVideoVariants();
  }

  public getAudioVideoCodec(): string | null {
    return this.mVideoManager.getAudioVideoCodec();
  }

  public async resumeFromHomeScreen(): Promise<void> {
    this.log("debug", "resumeFromHomeScreen()");
    const lastChannelNumber = await DeviceModel.getLastTunedChannelNumber();
    const channelNumber =
      lastChannelNumber === undefined || lastChannelNumber === null
        ? "1"
        : lastChannelNumber;
    const channel = await new ChannelsInteractor().tuneToChannel(channelNumber);
    this.startPlayback({
      channel,
      playbackContentType: PlaybackContentType.LIVE
    });
  }
}
